/**
 * Crime Ops – Tab "VIERTEL" (NT-VIERTEL-01, erweitert NT-HOTFIX-VIERTEL-01/NT-VIERTEL-02,
 * zur Schaltzentrale ausgebaut in NT-VIERTEL-03-SCHALTZENTRALE, Ecken/Dealer in
 * NT-VIERTEL-04-ECKEN)
 *
 * Aus der reinen Terminal-Liste wird der Ort, an dem gehandelt wird (F7): Bebauen (parzellen-
 * first — freie Parzelle anklicken, dann Typ wählen), Umziehen, Verkaufen und Öffnen laufen
 * jetzt hier. `pages/immobilien/lager.ts`/`verstecke.ts` sind seitdem reine Kataloge ohne
 * Kaufweg, verlinkt hierher. Parzellen haben eine Fläche in m² (F1/F2), die bestimmt, was
 * gebaut werden darf — Ausbau möglich (F3, "ERWEITERN AUF …"). Straßen sind einklappbar (F8,
 * Heimat standardmäßig zu) und zeigen oben einen Ticker der letzten eigenen Ereignisse (F9).
 *
 * Ecken (NT-VIERTEL-04-ECKEN): jede Straße hat eine Anzahl Dealer-Posten. Anwerben eines Dealers
 * bleibt im Dealer-Tab (crimeOps.ts), Postieren/Umsiedeln/Abziehen laufen hier (E3). Absatz-
 * Balken pro Straße ist rein aus der Straßenklasse abgeleitet (E6).
 *
 * Noch nicht in dieser Stufe: Präsenz/Gangs (Prompt 11), Brennpunkte (12), POIs — siehe CLAUDE.md.
 */

import { api, ApiError } from '../../services/api.js';
import { navigate } from '../../router.js';
import { showToast } from '../../utils/toast.js';
import { refreshBalance } from '../../utils/refreshBalance.js';
import { getSortedActions, type PresenceActionKey } from './viertel-presence-actions.js';
import { getDealerStatusLabelDe } from './dealer-status-labels.js';

// ─── Typen (Backend: viertel.service.ts::ViertelOverview / property.service.ts::CatalogResponse) ──

interface ParcelFlaecheAusbauView {
  isUpgrading: boolean;
  completesAt: string | null;
  nextFlaecheQm: number | null;
  nextCostCents: string | null;
  nextMinutes: number | null;
}

interface ViertelParcelView {
  id: string;
  houseNumber: number;
  propertyType: 'HIDEOUT' | 'WAREHOUSE';
  propertyId: string | null;
  label: string | null;
  nickname: string | null;
  isFree: boolean;
  flaecheQm: number;
  flaecheAusbau: ParcelFlaecheAusbauView;
}

// NT-VIERTEL-04-ECKEN, um usedSlots/effectiveSlots/vehicleLabel erweitert in
// NT-VIERTEL-07-FEINSCHLIFF (B5)
interface ViertelCornerView {
  id: string;
  name: string;
  isFree: boolean;
  dealerId: string | null;
  dealerName: string | null;
  dealerStatus: string | null;
  relocatingAwayCompletesAt: string | null;
  usedSlots: number | null;
  effectiveSlots: number | null;
  vehicleLabel: string | null;
  /** NT-VIERTEL-FIX-01 (V4): eigener Dealer — nur dann Buttons und Details. */
  isOwn: boolean;
  /** V4: Besitzer eines fremden Dealers laut Späher-Bericht, sonst null (= UNBEKANNT). */
  ownerLabel: string | null;
}

// NT-VIERTEL-05-PRAESENZ (PresenceActionKey lebt jetzt in viertel-presence-actions.ts)
type AbsatzLabel = 'GEDROSSELT' | 'NORMAL' | 'STARK';

interface StreetPresenceView {
  hasFaction: boolean;
  factionName: string | null;
  isRevealed: boolean;
  sharePercent: number | null;
  isLeading: boolean;
  isControlled: boolean;
  cooldowns: Record<PresenceActionKey, string | null>;
  costsCents: Record<PresenceActionKey, string>;
  // NT-VIERTEL-07-FEINSCHLIFF (B3) — server-berechnet aus dem tatsächlichen Verhältnis
  // Ist-Absatz ÷ bestmöglicher Absatz, nicht mehr rein aus streetClass abgeleitet.
  absatzLabel: AbsatzLabel;
}

interface ViertelStreetView {
  id: string;
  name: string;
  type: 'HEIMAT' | 'REVIER' | 'BRENNPUNKT';
  streetClass: 'RUHIG' | 'BELEBT' | 'HEISS';
  parcels: ViertelParcelView[];
  corners: ViertelCornerView[];
  distanceSteps: number | null;
  presence: StreetPresenceView;
}

// Minimaler Ausschnitt aus DealerResponse (crimeOps.ts) — nur die für POSTIEREN nötigen Felder.
interface PostableDealer {
  id: string;
  name: string;
  status: 'ACTIVE' | 'PAUSED' | 'TRAINING' | 'ARRESTED' | 'FLEEING' | 'GONE';
  streetCornerId: string | null;
}

interface ViertelOtherPropertyView {
  id: string;
  subType: string;
  label: string;
  nickname: string | null;
  kind: 'CAMPINGWAGEN' | 'NOTFALLGARAGE' | 'ALTBESTAND';
  propertyType: 'HIDEOUT' | 'WAREHOUSE';
}

interface EmergencyGarageStatusView {
  id: string;
  rentedAt: string;
  outstandingCents: string;
  currentHourlyRateCents: string;
  slots: number;
}

interface TickerEntryView {
  id: string;
  timestamp: string;
  action: string;
}

interface ViertelOverview {
  districtName: string | null;
  quarterName: string | null;
  streets: ViertelStreetView[];
  other: ViertelOtherPropertyView[];
  emergencyGarage: EmergencyGarageStatusView | null;
  ticker: TickerEntryView[];
}

// NT-VIERTEL-06-BRENNPUNKTE
// NT-VIERTEL-FIX-01 (V3): fremde Teilnehmer kommen ohne playerId — "UNBEKANNT A" oder Name aus
// dem Späher-Bericht, sharePercent null ohne Bericht.
interface BrennpunktParticipantView {
  label: string;
  isSelf: boolean;
  sharePercent: number | null;
  isController: boolean;
  isScouted: boolean;
}

interface BrennpunktListItem {
  streetId: string;
  streetName: string;
  districtName: string;
  distance: 0 | 1;
  occupiedCorners: number;
  totalCorners: number;
  controllerName: string | null;
  participantCount: number;
  scoutReport: { createdAt: string } | null;
  isFortified: boolean;
  ownDealerPosted: boolean;
  participants: BrennpunktParticipantView[];
  corners: ViertelCornerView[];
  absatzLabel: AbsatzLabel;
  presenceActions: { cooldowns: Record<PresenceActionKey, string | null>; costsCents: Record<PresenceActionKey, string> };
}

interface CatalogAxisRangeView { base: number; max: number; }

/** NT-POLISH-01 (P9): Bau-Vorschau, server-berechnet (property.service.ts::CatalogBuildPreview). */
interface CatalogBuildPreview {
  storageSlots:  CatalogAxisRangeView;
  growMaxLevel:  number;
  laborMaxLevel: number;
  vehicleSlots:  CatalogAxisRangeView;
}

interface CatalogWarehouseItem {
  subType: string;
  label: string;
  slots: number;
  buildPreview: CatalogBuildPreview;
  raidRiskPerFL: number;
  minFlaecheQm: number;
  purchasePriceCents: string;
  rentPerWeekCents: string;
  lore: string;
  ownedCount: number;
  canAfford: boolean;
}

interface CatalogHideoutItem {
  subType: string;
  label: string;
  purchasable: boolean;
  storage: CatalogAxisRangeView;
  camouflage: CatalogAxisRangeView;
  garage: CatalogAxisRangeView;
  buildPreview: CatalogBuildPreview;
  minFlaecheQm: number;
  raidRiskPerFL: number;
  purchasePriceCents: string;
  rentPerWeekCents: string;
  lore: string;
  ownedCount: number;
  canAfford: boolean;
}

interface CatalogResponse {
  warehouses: CatalogWarehouseItem[];
  hideouts: CatalogHideoutItem[];
}

// ─── Konstanten (reine Anzeige-Duplikate, kein Shared-Import — etabliertes Repo-Pattern,
// siehe auch pages/immobilien/lager.ts::maxW / verstecke.ts::MAX_HIDEOUTS) ────────────────

const MAX_WAREHOUSES = 5;
const MAX_HIDEOUTS = 3;

const TICKER_ACTION_LABELS: Record<string, string> = {
  BUY_PROPERTY: 'GEKAUFT',
  SELL_PROPERTY: 'VERKAUFT',
  PROPERTY_MOVE: 'UMZUG GESTARTET',
  STORAGE_UPGRADE: 'LAGER AUSGEBAUT',
  VEHICLE_SLOT_PURCHASE: 'FAHRZEUGSLOT GEKAUFT',
  HIDEOUT_AXIS_UPGRADE: 'PROFIL AUSGEBAUT',
  HIDEOUT_EXPANSION: 'GROW/LABOR AUSGEBAUT',
  RENT_EMERGENCY_GARAGE: 'NOTFALLGARAGE GEMIETET',
  PARCEL_UPGRADE: 'PARZELLE ERWEITERT',
  HIDEOUT_RAID_EXECUTED: 'RAZZIA — WARE BESCHLAGNAHMT',
  HIDEOUT_RAID_EXECUTED_EMPTY: 'RAZZIA — NICHTS GEFUNDEN',
  // NT-VIERTEL-05-PRAESENZ
  PRESENCE_ACTION_ZEIGEN: 'PRÄSENZ GEZEIGT',
  PRESENCE_ACTION_TAGS: 'TAGS GESPRÜHT',
  PRESENCE_ACTION_PARTY: 'RUNDE GESCHMISSEN',
  PRESENCE_ACTION_SPAEHER: 'SPÄHER GESCHICKT',
  STREET_TAKEOVER: 'FRAKTION VERDRÄNGT',
};

// ─── State ──────────────────────────────────────────────────────────────────

let overview: ViertelOverview | null = null;
let isLoading = true;
let loadError: string | null = null;
let refreshTimer: ReturnType<typeof setInterval> | null = null;
let reloadPromise: Promise<void> | null = null;

// F8: Straßen sind standardmäßig zu — Set enthält die AUFGEKLAPPTEN Straßen-IDs.
const expandedStreetIds = new Set<string>();

// BEBAUEN (parzellen-first, F7)
let catalogData: CatalogResponse | null = null;
let catalogLoading = false;
let catalogError: string | null = null;
interface BebauenModalState { parcelId: string; propertyType: 'HIDEOUT' | 'WAREHOUSE'; flaecheQm: number; }
let bebauenModal: BebauenModalState | null = null;
let bebauenBuyingSubType: string | null = null;

// UMZIEHEN — Zielauswahl kommt aus der bereits geladenen overview, kein zweiter API-Call nötig.
let moveOpenParcelId: string | null = null;
let moveSelectedParcelId = '';
let moveErrorMsg: string | null = null;
let moveSubmitting = false;

// VERKAUFEN
let confirmSellParcelId: string | null = null;
let sellSubmitting = false;

// ERWEITERN AUF … (Flächen-Ausbau, F3)
let expandConfirmParcelId: string | null = null;
let expandSubmitting = false;

// ─── Ecken (NT-VIERTEL-04-ECKEN) ────────────────────────────────────────────────

// POSTIEREN — lazy geladene Liste unpostierter Dealer (eigenständiger /dealer-Aufruf, kein
// Teil der overview — Ecken/Dealer sind unterschiedliche Domänen).
let postableDealers: PostableDealer[] | null = null;
let postableDealersLoading = false;
let postableDealersError: string | null = null;
let postOpenCornerId: string | null = null;
let postSelectedDealerId = '';
let postSubmitting = false;

// UMSIEDELN — Zielauswahl kommt aus der bereits geladenen overview (Muster: UMZIEHEN).
let relocateOpenDealerId: string | null = null;
let relocateSelectedCornerId = '';
let relocateErrorMsg: string | null = null;
let relocateSubmitting = false;

// ABZIEHEN
let confirmWithdrawDealerId: string | null = null;
let withdrawSubmitting = false;

// ─── Präsenz (NT-VIERTEL-05-PRAESENZ) ───────────────────────────────────────────

// Präsenz-Aktion (PRÄSENZ ZEIGEN/TAGS SPRÜHEN/RUNDE SCHMEISSEN/SPÄHER) — feuert direkt ohne
// Bestätigung (billige, wiederholbare Aktionen, kein destruktiver Effekt). Schlüssel: streetId.
const presenceActionSubmitting = new Set<string>();

// VERDRÄNGEN — inline Bestätigung (Muster: confirmSellParcelId), einmaliger, nicht rückgängig
// machbarer Effekt (Fraktion wird auf 0 zurückgesetzt).
let confirmTakeoverStreetId: string | null = null;
let takeoverSubmitting = false;

// ─── Brennpunkte (NT-VIERTEL-06-BRENNPUNKTE) ────────────────────────────────────

// Eigener, standardmäßig zugeklappter Top-Level-Abschnitt (Muster F8: Straßen sind ebenfalls
// standardmäßig zu) — lazy geladen, nur wenn der Spieler ihn tatsächlich aufklappt.
let brennpunkteExpanded = false;
let brennpunkte: BrennpunktListItem[] | null = null;
let brennpunkteLoading = false;
let brennpunkteError: string | null = null;
let expandedBrennpunktId: string | null = null; // welcher Brennpunkt ist aufgeklappt (Ecken/Teilnehmer sichtbar)

// ANSAGE (B7) — inline Bestätigung, danach ein lokaler Countdown bis effectiveAt (nur für die
// laufende Sitzung — kein Reload-übergreifendes Nachladen einer bereits laufenden Ansage in
// dieser Stufe, siehe CLAUDE.md offene Punkte).
let confirmAnsageStreetId: string | null = null;
let ansageSubmitting = false;
const announcedChallenges = new Map<string, string>(); // streetId → effectiveAt (ISO)

// ─── Helfer ───────────────────────────────────────────────────────────────────

function esc(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

function fmtEur(cents: string): string {
  return `${(Number(cents) / 100).toFixed(2)} ₦`;
}

function fmtCountdownUntil(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  const totalMinutes = Math.max(0, Math.ceil(ms / 60_000));
  const hh = Math.floor(totalMinutes / 60);
  const mm = totalMinutes % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function fmtTickerTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const STREET_TYPE_LABELS: Record<ViertelStreetView['type'], string> = {
  HEIMAT: 'HEIMAT',
  REVIER: 'REVIER',
  BRENNPUNKT: 'BRENNPUNKT',
};
const STREET_CLASS_LABELS: Record<ViertelStreetView['streetClass'], string> = {
  RUHIG: 'RUHIG',
  BELEBT: 'BELEBT',
  HEISS: 'HEISS',
};
const OTHER_KIND_LABELS: Record<ViertelOtherPropertyView['kind'], string> = {
  CAMPINGWAGEN: 'CAMPINGWAGEN',
  NOTFALLGARAGE: 'NOTFALLGARAGE',
  ALTBESTAND: 'ALTBESTAND',
};
// NT-VIERTEL-07-FEINSCHLIFF (B3) — kurzer Tooltip je Straßenklasse für das ABSATZ-Label. Die
// frühere, rein aus streetClass abgeleitete CLASS_ABSATZ_LABEL (RUHIG→GEDROSSELT) ist entfernt —
// das Label selbst kommt jetzt server-berechnet (ViertelStreetView.presence.absatzLabel /
// BrennpunktListItem.absatzLabel), diese Tooltips erklären nur noch, WAS die Straßenklasse an
// sich bedeutet.
const STREET_CLASS_TOOLTIPS: Record<ViertelStreetView['streetClass'], string> = {
  RUHIG: 'Straßenklasse RUHIG: weniger Laufkundschaft, dafür weniger Verdacht.',
  BELEBT: 'Straßenklasse BELEBT: durchschnittlicher Umsatz und Verdacht.',
  HEISS: 'Straßenklasse HEISS: mehr Umsatz, aber deutlich höherer Verdacht.',
};
// NT-VIERTEL-04-ECKEN — Dealer-Status deutsch lebt in dealer-status-labels.ts (Testbarkeit,
// s.o.) und wird oben importiert.

// ─── Präsenz (NT-VIERTEL-05-PRAESENZ) — reine Anzeige-Duplikate, Server bleibt autoritativ ────
// getSortedActions() (B1) lebt in viertel-presence-actions.ts (Testbarkeit, siehe dortiger
// Kommentar) und wird oben importiert.

const PRESENCE_ACTION_LABELS: Record<PresenceActionKey, string> = {
  ZEIGEN: 'PRÄSENZ ZEIGEN',
  TAGS: 'TAGS SPRÜHEN',
  PARTY: 'RUNDE SCHMEISSEN',
  SPAEHER: 'SPÄHER',
};
const PRESENCE_ACTION_HINTS: Record<PresenceActionKey, string> = {
  ZEIGEN: 'Etwas Einfluss, leichte Fahndung.',
  TAGS: 'Billig, wenig Einfluss, kleine Fahndung.',
  PARTY: 'Teuer, viel Einfluss, keine Fahndung.',
  SPAEHER: 'Kein Einfluss — deckt für eine Weile die genauen Anteile auf.',
};

/** Alle freien Ecken — eigene HEIMAT/REVIER-Ecken + (falls bereits geladen) erreichbare
 * Brennpunkt-Ecken (NT-VIERTEL-06-BRENNPUNKTE, B1/B2 — jede Ecke dort ist für jeden Spieler des
 * eigenen/Nachbarbezirks postierbar). Muster: getFreeParcelsOfType(). Die aktuell vom
 * umsiedelnden Dealer belegte Ecke taucht hier nie auf, sie gilt als nicht frei. */
function getFreeCorners(): { id: string; label: string }[] {
  const result: { id: string; label: string }[] = [];
  if (overview) {
    for (const street of overview.streets) {
      for (const c of street.corners) {
        if (!c.isFree) continue;
        result.push({ id: c.id, label: `${street.name} (${STREET_CLASS_LABELS[street.streetClass]}) · ${c.name}` });
      }
    }
  }
  if (brennpunkte) {
    for (const bp of brennpunkte) {
      for (const c of bp.corners) {
        if (!c.isFree) continue;
        result.push({ id: c.id, label: `${bp.streetName} (BRENNPUNKT, ${bp.districtName}) · ${c.name}` });
      }
    }
  }
  return result;
}

/** Alle freien eigenen Parzellen eines Typs, aus der bereits geladenen overview — kein zweiter
 * API-Call nötig (anders als die frühere Umzug-UI in lager.ts/verstecke.ts). */
function getFreeParcelsOfType(propertyType: ViertelParcelView['propertyType']): { id: string; label: string }[] {
  if (!overview) return [];
  const result: { id: string; label: string }[] = [];
  for (const street of overview.streets) {
    for (const p of street.parcels) {
      if (p.propertyType === propertyType && p.isFree) {
        result.push({
          id: p.id,
          label: `${street.name} (${STREET_CLASS_LABELS[street.streetClass]}) · Nr. ${String(p.houseNumber)} · ${String(p.flaecheQm)} m²`,
        });
      }
    }
  }
  return result;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

function addStyles(): void {
  if (document.getElementById('viertel-tab-styles')) return;
  const s = document.createElement('style');
  s.id = 'viertel-tab-styles';
  s.textContent = `
    #viertel-tab-root{font-family:var(--font-mono);}
    .vtl-header{font-family:var(--font-mono);font-size:var(--font-size-sm);letter-spacing:.06em;
      color:var(--text-primary);margin:0 0 var(--space-3);text-transform:uppercase;}
    .vtl-header .vtl-quarter{color:var(--accent-gold);}
    /* Teil C (NT-VIERTEL-07-FEINSCHLIFF): Fragezeichen-Link zum Kladde-Spickzettel. */
    .vtl-help-btn{margin-left:var(--space-2);width:20px;height:20px;line-height:18px;
      border-radius:var(--radius-btn);border:1px solid var(--text-muted);color:var(--text-muted);
      background:none;font-family:var(--font-mono);font-size:var(--font-size-xs);
      text-transform:none;cursor:pointer;padding:0;}
    .vtl-help-btn:hover{color:var(--accent-gold);border-color:var(--accent-gold);}

    .vtl-ticker{border:1px solid var(--border);border-radius:var(--radius);
      padding:var(--space-2) var(--space-3);margin-bottom:var(--space-3);background:var(--bg-elevated);}
    .vtl-ticker-title{font-size:var(--font-size-xs);letter-spacing:.08em;color:var(--text-muted);
      text-transform:uppercase;margin:0 0 6px;}
    .vtl-ticker-list{list-style:none;margin:0;padding:0;max-height:180px;overflow-y:auto;}
    .vtl-ticker-list li{display:flex;gap:var(--space-2);padding:2px 0;font-size:var(--font-size-xs);
      color:var(--text-muted);}
    .vtl-ticker-time{color:var(--text-italic);white-space:nowrap;}
    .vtl-ticker-action{color:var(--text-primary);}

    .vtl-street{border:1px solid var(--border);border-radius:var(--radius);
      margin-bottom:var(--space-2);background:var(--bg-elevated);overflow:hidden;}
    .vtl-street-head{display:flex;align-items:center;justify-content:space-between;gap:var(--space-2);
      flex-wrap:wrap;padding:var(--space-2) var(--space-3);cursor:pointer;}
    .vtl-street-name{font-size:var(--font-size-sm);letter-spacing:.05em;color:var(--text-primary);
      text-transform:uppercase;}
    .vtl-street-badges{display:flex;gap:6px;align-items:center;}
    .vtl-street-summary{font-size:var(--font-size-xs);color:var(--text-muted);white-space:nowrap;}
    .vtl-street-caret{font-size:var(--font-size-xs);color:var(--text-muted);}
    .vtl-street-body{padding:0 var(--space-3) var(--space-2);}

    .vtl-street--brennpunkt{border-color:var(--accent-red);border-left-width:3px;}
    .vtl-street--brennpunkt .vtl-street-name{color:var(--accent-red);}

    .vtl-badge{font-size:var(--font-size-xs);letter-spacing:.06em;padding:2px 6px;
      border-radius:var(--radius-sm);border:1px solid var(--border);color:var(--text-muted);
      text-transform:uppercase;white-space:nowrap;}
    .vtl-badge--heimat{border-color:var(--accent-blue);color:var(--accent-blue);}
    .vtl-badge--ruhig{border-color:var(--accent-green);color:var(--accent-green);}
    .vtl-badge--belebt{border-color:var(--accent-orange);color:var(--accent-orange);}
    .vtl-badge--heiss{border-color:var(--accent-red);color:var(--accent-red);}
    .vtl-badge--distance{border-color:var(--accent-blue);color:var(--accent-blue);}
    .vtl-badge--flaeche{border-color:var(--text-muted);color:var(--text-primary);}
    .vtl-badge--flaeche-max{border-color:var(--text-muted);color:var(--text-muted);}

    .vtl-parcel-row{padding:var(--space-2) 0;
      border-top:1px solid color-mix(in srgb, var(--border) 50%, transparent);}
    .vtl-parcel-row:first-of-type{border-top:none;}
    .vtl-parcel-main{display:flex;align-items:center;gap:var(--space-2);flex-wrap:wrap;}
    .vtl-parcel-nr{font-size:var(--font-size-xs);color:var(--text-muted);min-width:52px;}
    .vtl-parcel-type{font-size:var(--font-size-xs);color:var(--text-muted);min-width:80px;
      text-transform:uppercase;}
    .vtl-parcel-label{font-size:var(--font-size-sm);color:var(--text-primary);flex:1;min-width:120px;}
    .vtl-parcel-label.is-free{color:var(--text-italic);font-style:italic;}
    .vtl-parcel-actions{display:flex;gap:var(--space-2);flex-wrap:wrap;margin-top:6px;
      align-items:center;}
    .vtl-parcel-expand{margin-top:6px;}
    .vtl-sell-confirm{font-size:var(--font-size-xs);color:var(--accent-red);}

    /* Ecken (NT-VIERTEL-04-ECKEN) — gleiches Zeilen-Muster wie Parzellen. */
    .vtl-corner-row{padding:var(--space-2) 0;
      border-top:1px solid color-mix(in srgb, var(--border) 50%, transparent);}
    /* NT-VIERTEL-FIX-01 (V4): eigene Dealer mit Akzentrahmen, fremde gedämpft. */
    .vtl-corner-row--own{border-left:2px solid var(--accent-gold);padding-left:var(--space-2);}
    .vtl-corner-row--foreign .vtl-parcel-label{color:var(--text-muted);}
    .vtl-badge--own{border-color:var(--accent-gold);color:var(--accent-gold);}
    /* NT-VIERTEL-07-FEINSCHLIFF (B5): BESTAND + FAHRZEUG als kleine Zusatzzeile unter dem Label. */
    .vtl-corner-meta{width:100%;font-size:var(--font-size-xs);color:var(--text-muted);
      letter-spacing:.04em;}
    .vtl-absatz-row{display:flex;align-items:center;gap:var(--space-2);margin-bottom:var(--space-2);}
    .vtl-absatz-label{font-size:var(--font-size-xs);letter-spacing:.06em;color:var(--text-muted);
      text-transform:uppercase;}
    .vtl-absatz-bar{display:flex;gap:3px;}
    .vtl-absatz-seg{width:16px;height:6px;border-radius:var(--radius-sm);
      border:1px solid var(--border);background:transparent;}
    .vtl-absatz-seg.is-filled{background:var(--accent-orange);border-color:var(--accent-orange);}

    /* Präsenz (NT-VIERTEL-05-PRAESENZ) */
    .vtl-presence{margin-top:var(--space-3);padding-top:var(--space-2);border-top:1px solid var(--border);}
    .vtl-presence-bar-row{display:flex;align-items:center;gap:var(--space-2);margin-bottom:6px;
      flex-wrap:wrap;}
    .vtl-presence-faction{font-size:var(--font-size-xs);color:var(--text-muted);
      text-transform:uppercase;letter-spacing:.05em;}
    .vtl-presence-track{flex:1;min-width:80px;height:8px;border-radius:var(--radius-sm);
      border:1px solid var(--border);background:var(--bg-surface);overflow:hidden;position:relative;}
    .vtl-presence-fill{height:100%;background:var(--accent-green);}
    .vtl-presence-fill.is-behind{background:var(--accent-red);}
    .vtl-presence-status{font-size:var(--font-size-xs);letter-spacing:.06em;text-transform:uppercase;}
    .vtl-presence-status--leading{color:var(--accent-green);}
    .vtl-presence-status--behind{color:var(--accent-red);}
    .vtl-presence-controlled{font-size:var(--font-size-xs);color:var(--accent-gold);
      text-transform:uppercase;letter-spacing:.05em;margin:0 0 var(--space-2);}

    .vtl-action-grid{display:grid;grid-template-columns:repeat(auto-fill, minmax(150px, 1fr));
      gap:var(--space-2);}
    .vtl-action-btn{font-family:var(--font-mono);font-size:var(--font-size-xs);
      letter-spacing:.04em;text-transform:uppercase;padding:6px 10px;min-height:44px;
      background:transparent;border-radius:var(--radius-btn);cursor:pointer;
      border:1px solid var(--accent-purple);color:var(--accent-purple);
      display:flex;flex-direction:column;align-items:flex-start;gap:2px;}
    .vtl-action-btn:disabled{opacity:.4;cursor:not-allowed;}
    .vtl-action-btn-cost{font-size:var(--font-size-xs);color:var(--text-muted);}
    .vtl-action-hint{font-size:var(--font-size-xs);color:var(--text-italic);margin:6px 0 0;}

    .vtl-move-row{margin-top:var(--space-2);padding-top:var(--space-2);border-top:1px solid var(--border);}
    .vtl-move-label{display:block;font-size:var(--font-size-xs);color:var(--text-muted);
      letter-spacing:.08em;text-transform:uppercase;margin-bottom:6px;}
    .vtl-move-select{width:100%;font-family:var(--font-mono);font-size:var(--font-size-xs);
      background:var(--bg-surface);border:1px solid var(--border);border-radius:var(--radius-btn);
      color:var(--text-primary);padding:var(--space-2);margin-bottom:var(--space-2);}
    .vtl-move-hint{font-size:var(--font-size-xs);color:var(--text-muted);margin:0;}
    .vtl-move-error{font-size:var(--font-size-xs);color:var(--accent-red);margin:0 0 var(--space-2);}

    .vtl-expand-status{font-size:var(--font-size-xs);color:var(--accent-orange);letter-spacing:.05em;}
    .vtl-expand-status--max{color:var(--text-muted);}
    .vtl-expand-confirm{display:flex;align-items:center;gap:var(--space-2);flex-wrap:wrap;
      font-size:var(--font-size-xs);color:var(--text-muted);}

    .vtl-other-section{margin-top:var(--space-4);}
    .vtl-other-title{font-size:var(--font-size-xs);letter-spacing:.08em;color:var(--text-muted);
      text-transform:uppercase;margin:0 0 var(--space-2);}
    .vtl-other-row{display:flex;align-items:center;gap:var(--space-2);padding:4px 0;flex-wrap:wrap;}
    .vtl-other-kind{font-size:var(--font-size-xs);color:var(--accent-blue);min-width:110px;
      text-transform:uppercase;}
    .vtl-empty{font-size:var(--font-size-sm);color:var(--text-muted);padding:var(--space-3) 0;}

    .vtl-eg-bar{border:1px solid var(--accent-orange);border-radius:var(--radius);
      padding:var(--space-2) var(--space-3);margin-bottom:var(--space-3);background:var(--bg-elevated);}
    .vtl-eg-title{font-size:var(--font-size-xs);letter-spacing:.08em;color:var(--accent-orange);
      text-transform:uppercase;margin:0 0 6px;}
    .vtl-eg-grid{display:grid;grid-template-columns:1fr 1fr;gap:4px var(--space-3);}
    .vtl-eg-key{font-size:var(--font-size-xs);color:var(--text-muted);text-transform:uppercase;}
    .vtl-eg-val{font-size:var(--font-size-sm);color:var(--text-primary);}
    .vtl-eg-val.is-debt{color:var(--accent-red);}

    .vtl-btn{font-family:var(--font-mono);font-size:var(--font-size-xs);letter-spacing:.07em;
      text-transform:uppercase;padding:6px 14px;background:transparent;min-height:36px;
      border-radius:var(--radius-btn);cursor:pointer;border:1px solid var(--border);
      color:var(--text-muted);}
    .vtl-btn--manage{border-color:var(--accent-blue);color:var(--accent-blue);}
    .vtl-btn--buy{border-color:var(--accent-green);color:var(--accent-green);}
    .vtl-btn--sell{border-color:var(--accent-red);color:var(--accent-red);}
    .vtl-btn--danger{border-color:var(--accent-red);color:var(--accent-red);}
    .vtl-btn--confirm{border-color:var(--accent-green);color:var(--accent-green);}
    .vtl-btn--expand{border-color:var(--accent-orange);color:var(--accent-orange);}
    .vtl-btn--cancel{border-color:var(--accent-red);color:var(--accent-red);margin-top:var(--space-2);}
    .vtl-btn:disabled{opacity:.4;cursor:not-allowed;}

    /* BEBAUEN-Modal — gemeinsame .modal-backdrop/.modal-Hülle, eigene Item-Klassen */
    .vtl-bebauen-item{border:1px solid var(--border);border-radius:var(--radius);
      padding:var(--space-2) var(--space-3);margin-bottom:var(--space-2);background:var(--bg-surface);}
    .vtl-bebauen-item--blocked{opacity:.5;}
    .vtl-bebauen-item-head{display:flex;justify-content:space-between;align-items:baseline;
      gap:var(--space-2);margin-bottom:2px;}
    .vtl-bebauen-item-title{font-size:var(--font-size-sm);color:var(--text-primary);letter-spacing:.04em;}
    .vtl-bebauen-item-price{font-size:var(--font-size-sm);color:var(--accent-gold);}
    .vtl-bebauen-item-meta{font-size:var(--font-size-xs);color:var(--text-muted);margin:0 0 6px;}
    .vtl-build-preview{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:2px 12px;font-size:var(--font-size-xs);color:var(--text-muted);margin:0 0 6px;}
    .vtl-build-preview strong{color:var(--text-primary);font-weight:normal;}
    .vtl-bebauen-item-block{font-size:var(--font-size-xs);color:var(--accent-orange);margin:0 0 6px;}
  `;
  document.head.appendChild(s);
}

// ─── Rendering: Ticker (F9) ────────────────────────────────────────────────────

function renderTicker(entries: TickerEntryView[]): string {
  if (entries.length === 0) return '';
  return `<div class="vtl-ticker">
    <p class="vtl-ticker-title">LETZTE EREIGNISSE</p>
    <ul class="vtl-ticker-list">
      ${entries.map((e) => `<li>
        <span class="vtl-ticker-time">${esc(fmtTickerTime(e.timestamp))}</span>
        <span class="vtl-ticker-action">${esc(TICKER_ACTION_LABELS[e.action] ?? e.action)}</span>
      </li>`).join('')}
    </ul>
  </div>`;
}

// ─── Rendering: Parzelle ────────────────────────────────────────────────────────

function renderFlaecheAusbauControl(parcel: ViertelParcelView): string {
  const fa = parcel.flaecheAusbau;
  if (fa.isUpgrading && fa.completesAt) {
    return `<span class="vtl-expand-status">AUSBAU LÄUFT — noch ${fmtCountdownUntil(fa.completesAt)}</span>`;
  }
  if (fa.nextFlaecheQm === null) {
    // NT-VIERTEL-07-FEINSCHLIFF (B6): "MAX" steht jetzt direkt im Flächen-Badge oben in
    // renderParcelRow() — hier keine zweite Anzeige derselben Information mehr.
    return '';
  }
  if (expandConfirmParcelId === parcel.id) {
    const costLabel = fa.nextCostCents !== null ? fmtEur(fa.nextCostCents) : '?';
    return `<span class="vtl-expand-confirm">
      Auf ${String(fa.nextFlaecheQm)} m² erweitern für ${costLabel} (${String(fa.nextMinutes ?? '?')} min)?
      <button class="vtl-btn vtl-btn--confirm" data-vtl-expand-confirm="${esc(parcel.id)}" ${expandSubmitting ? 'disabled' : ''}>BESTÄTIGEN</button>
      <button class="vtl-btn" data-vtl-expand-cancel>ABBRECHEN</button>
    </span>`;
  }
  return `<button class="vtl-btn vtl-btn--expand" data-vtl-expand="${esc(parcel.id)}">ERWEITERN AUF ${String(fa.nextFlaecheQm)} m²</button>`;
}

function renderMoveSection(parcel: ViertelParcelView): string {
  const options = getFreeParcelsOfType(parcel.propertyType);
  if (options.length === 0) {
    return `<div class="vtl-move-row">
      <p class="vtl-move-hint">Keine freie eigene Parzelle dieses Typs verfügbar.</p>
      <div class="vtl-parcel-actions"><button class="vtl-btn" data-vtl-move-cancel>SCHLIESSEN</button></div>
    </div>`;
  }
  const opts = options
    .map((o) => `<option value="${esc(o.id)}" ${o.id === moveSelectedParcelId ? 'selected' : ''}>${esc(o.label)}</option>`)
    .join('');
  return `<div class="vtl-move-row">
    <label class="vtl-move-label">ZIEL-PARZELLE</label>
    <select class="vtl-move-select" data-vtl-move-select="${esc(parcel.id)}">${opts}</select>
    ${moveErrorMsg ? `<p class="vtl-move-error">${esc(moveErrorMsg)}</p>` : ''}
    <div class="vtl-parcel-actions">
      <button class="vtl-btn vtl-btn--manage" data-vtl-move-confirm="${esc(parcel.id)}" ${!moveSelectedParcelId || moveSubmitting ? 'disabled' : ''}>BESTÄTIGEN</button>
      <button class="vtl-btn" data-vtl-move-cancel>ABBRECHEN</button>
    </div>
  </div>`;
}

function renderParcelRow(parcel: ViertelParcelView): string {
  const typeLabel = parcel.propertyType === 'HIDEOUT' ? 'VERSTECK' : 'LAGER';
  // NT-VIERTEL-07-FEINSCHLIFF (B6): "MAX" sitzt jetzt direkt im Flächen-Badge, nur einmal je
  // Parzelle (siehe renderFlaecheAusbauControl()).
  const isMaxFlaeche = parcel.flaecheAusbau.nextFlaecheQm === null;
  const flaecheBadge = `<span class="vtl-badge vtl-badge--flaeche${isMaxFlaeche ? ' vtl-badge--flaeche-max' : ''}">${String(parcel.flaecheQm)} m²${isMaxFlaeche ? ' · MAX' : ''}</span>`;

  if (parcel.isFree) {
    return `<div class="vtl-parcel-row">
      <div class="vtl-parcel-main">
        <span class="vtl-parcel-nr">Nr. ${String(parcel.houseNumber)}</span>
        <span class="vtl-parcel-type">${typeLabel}</span>
        <span class="vtl-parcel-label is-free">FREI</span>
        ${flaecheBadge}
      </div>
      <div class="vtl-parcel-actions">
        <button class="vtl-btn vtl-btn--buy" data-vtl-bebauen="${esc(parcel.id)}"
          data-vtl-bebauen-type="${parcel.propertyType}" data-vtl-bebauen-flaeche="${String(parcel.flaecheQm)}">${typeLabel} BAUEN</button>
      </div>
      <div class="vtl-parcel-expand">${renderFlaecheAusbauControl(parcel)}</div>
    </div>`;
  }

  const labelText = `${parcel.label ?? ''}${parcel.nickname ? ` · ${parcel.nickname}` : ''}`;
  const propertyId = parcel.propertyId ?? '';

  let actionsHtml: string;
  if (confirmSellParcelId === parcel.id) {
    actionsHtml = `
      <span class="vtl-sell-confirm">Wirklich verkaufen?</span>
      <button class="vtl-btn vtl-btn--danger" data-vtl-sell-confirm="${esc(parcel.id)}" ${sellSubmitting ? 'disabled' : ''}>JA</button>
      <button class="vtl-btn" data-vtl-sell-cancel>NEIN</button>`;
  } else {
    actionsHtml = `
      <button class="vtl-btn vtl-btn--manage" data-vtl-open="${esc(propertyId)}" data-vtl-open-type="${parcel.propertyType}">ÖFFNEN</button>
      <button class="vtl-btn" data-vtl-move="${esc(parcel.id)}" data-vtl-move-type="${parcel.propertyType}">UMZIEHEN</button>
      <button class="vtl-btn vtl-btn--sell" data-vtl-sell="${esc(parcel.id)}">VERKAUFEN</button>`;
  }

  return `<div class="vtl-parcel-row">
    <div class="vtl-parcel-main">
      <span class="vtl-parcel-nr">Nr. ${String(parcel.houseNumber)}</span>
      <span class="vtl-parcel-type">${typeLabel}</span>
      <span class="vtl-parcel-label">${esc(labelText)}</span>
      ${flaecheBadge}
    </div>
    <div class="vtl-parcel-actions">${actionsHtml}</div>
    ${moveOpenParcelId === parcel.id ? renderMoveSection(parcel) : ''}
    <div class="vtl-parcel-expand">${renderFlaecheAusbauControl(parcel)}</div>
  </div>`;
}

// ─── Rendering: Ecken (NT-VIERTEL-04-ECKEN) ────────────────────────────────────

/** Dealer, die postierbar sind (kein Posten, ACTIVE/PAUSED) — geteilt zwischen Render und
 * Bestätigen, damit beide nie auseinanderdriften (gleiche Quelle für Fallback-Auswahl). */
function getPostableDealerOptions(): PostableDealer[] {
  return (postableDealers ?? []).filter((d) => d.streetCornerId === null && (d.status === 'ACTIVE' || d.status === 'PAUSED'));
}

function renderPostSection(corner: ViertelCornerView): string {
  if (postableDealersError) {
    return `<div class="vtl-move-row"><p class="vtl-move-error">${esc(postableDealersError)}</p>
      <div class="vtl-parcel-actions"><button class="vtl-btn" data-vtl-post-cancel>SCHLIESSEN</button></div></div>`;
  }
  if (postableDealersLoading || postableDealers === null) {
    return `<div class="vtl-move-row"><p class="vtl-move-hint">LADE DEALER...</p></div>`;
  }
  const options = getPostableDealerOptions();
  if (options.length === 0) {
    return `<div class="vtl-move-row">
      <p class="vtl-move-hint">Kein unpostierter Dealer verfügbar.</p>
      <div class="vtl-parcel-actions"><button class="vtl-btn" data-vtl-post-cancel>SCHLIESSEN</button></div>
    </div>`;
  }
  // Fallback auf den ersten verfügbaren Dealer, solange der Spieler noch keine eigene Auswahl
  // getroffen hat — verhindert, dass BESTÄTIGEN bei genau einem Dealer dauerhaft deaktiviert
  // bleibt (der Browser zeigt die erste <option> optisch bereits "selected" an, ohne dass ein
  // change-Event feuert, solange niemand eine ANDERE Option wählt).
  const selectedId = options.some((d) => d.id === postSelectedDealerId) ? postSelectedDealerId : (options[0]?.id ?? '');
  const opts = options
    .map((d) => `<option value="${esc(d.id)}" ${d.id === selectedId ? 'selected' : ''}>${esc(d.name)}</option>`)
    .join('');
  return `<div class="vtl-move-row">
    <label class="vtl-move-label">DEALER</label>
    <select class="vtl-move-select" data-vtl-post-select="${esc(corner.id)}">${opts}</select>
    <div class="vtl-parcel-actions">
      <button class="vtl-btn vtl-btn--manage" data-vtl-post-confirm="${esc(corner.id)}" ${!selectedId || postSubmitting ? 'disabled' : ''}>BESTÄTIGEN</button>
      <button class="vtl-btn" data-vtl-post-cancel>ABBRECHEN</button>
    </div>
  </div>`;
}

function renderRelocateSection(dealerId: string): string {
  const options = getFreeCorners();
  if (options.length === 0) {
    return `<div class="vtl-move-row">
      <p class="vtl-move-hint">Keine freie eigene Ecke verfügbar.</p>
      <div class="vtl-parcel-actions"><button class="vtl-btn" data-vtl-relocate-cancel>SCHLIESSEN</button></div>
    </div>`;
  }
  const opts = options
    .map((o) => `<option value="${esc(o.id)}" ${o.id === relocateSelectedCornerId ? 'selected' : ''}>${esc(o.label)}</option>`)
    .join('');
  return `<div class="vtl-move-row">
    <label class="vtl-move-label">ZIEL-ECKE</label>
    <select class="vtl-move-select" data-vtl-relocate-select="${esc(dealerId)}">${opts}</select>
    ${relocateErrorMsg ? `<p class="vtl-move-error">${esc(relocateErrorMsg)}</p>` : ''}
    <div class="vtl-parcel-actions">
      <button class="vtl-btn vtl-btn--manage" data-vtl-relocate-confirm="${esc(dealerId)}" ${!relocateSelectedCornerId || relocateSubmitting ? 'disabled' : ''}>BESTÄTIGEN</button>
      <button class="vtl-btn" data-vtl-relocate-cancel>ABBRECHEN</button>
    </div>
  </div>`;
}

function renderCornerRow(corner: ViertelCornerView): string {
  // NT-VIERTEL-FIX-01 (V4): fremder Dealer — ohne Namen/Bestand/Fahrzeug, ohne Buttons. Besitzer
  // nur, wenn ein Späher-Bericht ihn kennt.
  if (!corner.isFree && !corner.isOwn) {
    return `<div class="vtl-corner-row vtl-corner-row--foreign">
      <div class="vtl-parcel-main">
        <span class="vtl-parcel-type">ECKE</span>
        <span class="vtl-parcel-label">${esc(corner.name)} · ${corner.ownerLabel ? `DEALER VON ${esc(corner.ownerLabel)}` : 'UNBEKANNTER DEALER'}</span>
      </div>
    </div>`;
  }

  if (corner.isFree) {
    return `<div class="vtl-corner-row">
      <div class="vtl-parcel-main">
        <span class="vtl-parcel-type">ECKE</span>
        <span class="vtl-parcel-label is-free">${esc(corner.name)} · FREI</span>
      </div>
      <div class="vtl-parcel-actions">
        <button class="vtl-btn vtl-btn--buy" data-vtl-post="${esc(corner.id)}">POSTIEREN</button>
      </div>
      ${postOpenCornerId === corner.id ? renderPostSection(corner) : ''}
    </div>`;
  }

  const dealerId = corner.dealerId ?? '';
  const isRelocating = corner.relocatingAwayCompletesAt !== null;
  // NT-VIERTEL-07-FEINSCHLIFF (B5): deutscher Status statt rohem Enum-Wert.
  const statusLabel = getDealerStatusLabelDe(corner.dealerStatus ?? '');

  let actionsHtml: string;
  if (isRelocating) {
    actionsHtml = `<span class="vtl-expand-status">SIEDELT UM — noch ${fmtCountdownUntil(corner.relocatingAwayCompletesAt ?? '')}</span>`;
  } else if (confirmWithdrawDealerId === dealerId) {
    actionsHtml = `
      <span class="vtl-sell-confirm">Dealer wirklich abziehen?</span>
      <button class="vtl-btn vtl-btn--danger" data-vtl-withdraw-confirm="${esc(dealerId)}" ${withdrawSubmitting ? 'disabled' : ''}>JA</button>
      <button class="vtl-btn" data-vtl-withdraw-cancel>NEIN</button>`;
  } else {
    // NT-VIERTEL-07-FEINSCHLIFF (B5): BELIEFERN/FAHRZEUG ergänzt — beide springen ins DEALER-Tab
    // und öffnen dort das jeweilige Panel für genau diesen Dealer (Muster: handleOpen() springt
    // ebenso über sessionStorage in eine andere Crime-Ops-Sektion).
    actionsHtml = `
      <button class="vtl-btn vtl-btn--manage" data-vtl-corner-supply="${esc(dealerId)}">BELIEFERN</button>
      <button class="vtl-btn" data-vtl-corner-vehicle="${esc(dealerId)}">FAHRZEUG</button>
      <button class="vtl-btn" data-vtl-relocate="${esc(dealerId)}">UMSIEDELN</button>
      <button class="vtl-btn vtl-btn--sell" data-vtl-withdraw="${esc(dealerId)}">ABZIEHEN</button>`;
  }

  // NT-VIERTEL-07-FEINSCHLIFF (B5): Bestand in Slots + Fahrzeug als Zusatz, direkt an der Ecke.
  const bestandMeta = corner.effectiveSlots !== null
    ? `<span class="vtl-corner-meta">BESTAND ${String(corner.usedSlots ?? 0)}/${String(corner.effectiveSlots)}${corner.vehicleLabel ? ` · ${esc(corner.vehicleLabel.toUpperCase())}` : ''}</span>`
    : '';

  return `<div class="vtl-corner-row vtl-corner-row--own">
    <div class="vtl-parcel-main">
      <span class="vtl-parcel-type">ECKE</span>
      <span class="vtl-badge vtl-badge--own">DEIN DEALER</span>
      <span class="vtl-parcel-label">${esc(corner.name)} · ${esc(corner.dealerName ?? '')} (${esc(statusLabel)})</span>
      ${bestandMeta}
    </div>
    <div class="vtl-parcel-actions">${actionsHtml}</div>
    ${!isRelocating && relocateOpenDealerId === dealerId ? renderRelocateSection(dealerId) : ''}
  </div>`;
}

/** E6: "ABSATZ: GEDROSSELT/NORMAL/STARK" mit Balken — keine Prozentzahl, keine Reinheit, keine
 * Grammzahl. Gilt für die ganze Straße, nicht pro Ecke. Der Balken (1/2/3 Segmente) zeigt weiter
 * das Potenzial der Straßenklasse selbst (RUHIG/BELEBT/HEISS), das Textlabel daneben kommt seit
 * NT-VIERTEL-07-FEINSCHLIFF (B3) server-berechnet aus dem tatsächlichen Ist/Best-Verhältnis —
 * eine ruhige, aber unbedrängte eigene Straße zeigt dadurch nicht mehr fälschlich GEDROSSELT. */
function renderAbsatzBar(streetClass: ViertelStreetView['streetClass'], absatzLabel: AbsatzLabel): string {
  const level = streetClass === 'RUHIG' ? 1 : streetClass === 'BELEBT' ? 2 : 3;
  const segments = [1, 2, 3]
    .map((i) => `<span class="vtl-absatz-seg${i <= level ? ' is-filled' : ''}"></span>`)
    .join('');
  return `<div class="vtl-absatz-row">
    <span class="vtl-absatz-label" title="${esc(STREET_CLASS_TOOLTIPS[streetClass])}">ABSATZ: ${absatzLabel}</span>
    <div class="vtl-absatz-bar">${segments}</div>
  </div>`;
}

/** NT-VIERTEL-05-PRAESENZ — Einflussbalken + Fraktionsname + Verdrängen-Button (nur bei
 * hasFaction, aktuell nur REVIER) + Aktionsleiste (REVIER/BRENNPUNKT, P3). B4/NT-VIERTEL-07-
 * FEINSCHLIFF: auf HEIMAT gibt es keinen Gegner und keinen Einfluss zu verteidigen — keine
 * Aktionsleiste (Server lehnt einen Versuch trotzdem zusätzlich mit 403 ab). P4: ohne SPÄHER
 * nur "FÜHREND/NICHT FÜHREND", keine genaue Zahl. P6: auf REVIER-Straßen verkaufen postierte
 * Dealer unabhängig vom Kontroll-Status gedrosselt (calcRevierThrottleFactor, nie 0) — Hinweis
 * dazu unabhängig von hasFaction/isControlled, solange der Anteil nicht bei 100% liegt. */
function renderPresenceSection(street: ViertelStreetView): string {
  if (street.type === 'HEIMAT') return '';
  const p = street.presence;

  const revierHint = street.type === 'REVIER'
    ? `<p class="vtl-action-hint">Dealer verkaufen hier gedrosselt — mehr Einfluss erhöht den Absatz.</p>`
    : '';

  let factionBlock = '';
  if (p.hasFaction) {
    const fillPercent = p.isRevealed && p.sharePercent !== null ? p.sharePercent : (p.isLeading ? 100 : 0);
    const statusLabel = p.isRevealed && p.sharePercent !== null
      ? `${p.sharePercent.toFixed(1)} %`
      : (p.isLeading ? 'FÜHREND' : 'NICHT FÜHREND');
    const statusCls = p.isLeading ? 'vtl-presence-status--leading' : 'vtl-presence-status--behind';
    const fillCls = p.isLeading ? '' : ' is-behind';

    const takeoverHtml = confirmTakeoverStreetId === street.id
      ? `<span class="vtl-sell-confirm">Fraktion wirklich verdrängen?</span>
         <button class="vtl-btn vtl-btn--danger" data-vtl-takeover-confirm="${esc(street.id)}" ${takeoverSubmitting ? 'disabled' : ''}>JA</button>
         <button class="vtl-btn" data-vtl-takeover-cancel>NEIN</button>`
      : (p.isLeading
        ? `<button class="vtl-btn vtl-btn--danger" data-vtl-takeover="${esc(street.id)}">VERDRÄNGEN</button>`
        : '');

    // Balken = EIGENER Anteil. Vorher stand der Fraktionsname davor, was "Gegner hat 100 %"
    // las — Gegner steht deshalb jetzt in einer eigenen Zeile darüber.
    factionBlock = `
      <p class="vtl-action-hint">GEGNER: ${esc((p.factionName ?? 'Fraktion').toUpperCase())}</p>
      <div class="vtl-presence-bar-row">
        <span class="vtl-presence-faction">DEIN EINFLUSS</span>
        <div class="vtl-presence-track"><div class="vtl-presence-fill${fillCls}" style="width:${String(Math.round(fillPercent))}%;"></div></div>
        <span class="vtl-presence-status ${statusCls}">${statusLabel}</span>
      </div>
      ${p.isControlled ? `<p class="vtl-presence-controlled">DU KONTROLLIERST DIESE STRASSE</p>` : ''}
      <div class="vtl-parcel-actions">${takeoverHtml}</div>
    `;
  }

  const actionButtons = renderPresenceActionButtons(street.id, p.cooldowns, p.costsCents);

  return `<div class="vtl-presence">
    ${revierHint}
    ${factionBlock}
    <div class="vtl-action-grid">${actionButtons}</div>
  </div>`;
}

/** Geteilte Aktionsleiste (ZEIGEN/TAGS/PARTY/SPÄHER) — genutzt von HEIMAT/REVIER
 * (renderPresenceSection) UND Brennpunkten (renderBrennpunktCard). Nur die Cooldown-/Kosten-Quelle
 * unterscheidet sich (Straßen-Presence vs. `BrennpunktListItem.presenceActions`), der Klick-Handler
 * (`handlePresenceAction`) ist derselbe generische `POST /viertel/streets/:streetId/action`. */
function renderPresenceActionButton(
  streetId: string,
  action: PresenceActionKey,
  cooldownIso: string | null,
  costCents: string,
): string {
  const onCooldown = cooldownIso !== null && new Date(cooldownIso).getTime() > Date.now();
  const busy = presenceActionSubmitting.has(`${streetId}:${action}`);
  const costLabel = fmtEur(costCents);
  // NT-VIERTEL-07-FEINSCHLIFF (B2): "NOCH … STD" statt "Cooldown …" — Serverzeit als Quelle,
  // lokal aus dem gelieferten ISO-Zeitpunkt berechnet, kein eigener Timer-Zustand; der 30s-
  // Reload (attachTabListeners()::refreshTimer) hält den Countdown minutengenau "live".
  const statusText = onCooldown ? `NOCH ${fmtCountdownUntil(cooldownIso)} STD` : costLabel;
  return `<button class="vtl-action-btn" data-vtl-presence-action="${esc(streetId)}"
    data-vtl-presence-action-key="${action}" title="${esc(PRESENCE_ACTION_HINTS[action])}"
    ${onCooldown || busy ? 'disabled' : ''}>
    <span>${PRESENCE_ACTION_LABELS[action]}</span>
    <span class="vtl-action-btn-cost">${esc(statusText)}</span>
  </button>`;
}

function renderPresenceActionButtons(
  streetId: string,
  cooldowns: Record<PresenceActionKey, string | null>,
  costsCents: Record<PresenceActionKey, string>,
): string {
  return getSortedActions(costsCents)
    .map((action) => renderPresenceActionButton(streetId, action, cooldowns[action], costsCents[action]))
    .join('');
}

// ─── Rendering: Brennpunkte (NT-VIERTEL-06-BRENNPUNKTE) ────────────────────────────

/** Balken je Teilnehmer, absteigend nach Anteil — eigener Eintrag hervorgehoben, Kontrolleur
 * gold. Muster: Präsenz-Balken (renderPresenceSection), aber für mehrere Spieler statt einem
 * Spieler gegen eine Fraktion. */
/** "VOR 3 STD" / "VOR 12 MIN" — Alter eines Späher-Berichts. */
function fmtAgo(iso: string): string {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return `VOR ${String(minutes)} MIN`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `VOR ${String(hours)} STD`;
  return `VOR ${String(Math.floor(hours / 24))} TAGEN`;
}

/** NT-VIERTEL-FIX-01 (V3): eigener Anteil live, fremde ohne Späher als "UNBEKANNT A · ?? %",
 * mit Späher-Bericht Name + eingefrorener Wert vom Einsatzzeitpunkt. */
function renderBrennpunktParticipants(bp: BrennpunktListItem): string {
  if (bp.participants.length === 0) {
    return `<p class="vtl-move-hint">Noch keine Teilnehmer — postiere einen Dealer, um einzusteigen.</p>`;
  }
  const reportLine = bp.scoutReport
    ? `<p class="vtl-action-hint">SPÄHER-BERICHT · STAND: ${fmtAgo(bp.scoutReport.createdAt)} — Werte eingefroren.</p>`
    : `<p class="vtl-action-hint">${String(bp.participantCount)} TEILNEHMER · fremde Anteile unbekannt — SPÄHER schafft Klarheit.</p>`;
  const rows = bp.participants.map((p) => {
    const cls = p.isController ? 'vtl-presence-status--leading' : (p.isSelf ? '' : 'vtl-presence-status--behind');
    const valueLabel = p.sharePercent === null ? '?? %' : `${p.sharePercent.toFixed(1)} %`;
    const fillWidth = p.sharePercent === null ? 0 : Math.round(p.sharePercent);
    return `<div class="vtl-presence-bar-row">
      <span class="vtl-presence-faction">${esc(p.label)}${p.isController ? ' · KONTROLLEUR' : ''}</span>
      <div class="vtl-presence-track"><div class="vtl-presence-fill${p.isController ? '' : ' is-behind'}" style="width:${String(fillWidth)}%;"></div></div>
      <span class="vtl-presence-status ${cls}">${valueLabel}</span>
    </div>`;
  }).join('');
  return `<div class="vtl-presence">${reportLine}${rows}</div>`;
}

/** ANSAGE (B7) — nur relevant, wenn gefestigt UND der Spieler nicht selbst Kontrolleur ist. */
function renderAnsageControl(bp: BrennpunktListItem): string {
  if (!bp.isFortified || bp.participants.find((p) => p.isController)?.isSelf) return '';

  const announcedAt = announcedChallenges.get(bp.streetId);
  if (announcedAt) {
    const isEffective = new Date(announcedAt).getTime() <= Date.now();
    return `<p class="vtl-action-hint">
      ${isEffective ? 'Ansage wirksam — deine Aktionen zählen jetzt hier.' : `ANSAGE LÄUFT — wirksam in ${fmtCountdownUntil(announcedAt)}`}
    </p>`;
  }

  if (confirmAnsageStreetId === bp.streetId) {
    return `<span class="vtl-sell-confirm">Straße ist gefestigt — Ansage machen (Verteidiger wird benachrichtigt, wirkt erst nach 24 Std.)?</span>
      <button class="vtl-btn vtl-btn--confirm" data-vtl-ansage-confirm="${esc(bp.streetId)}" ${ansageSubmitting ? 'disabled' : ''}>ANSAGE MACHEN</button>
      <button class="vtl-btn" data-vtl-ansage-cancel>ABBRECHEN</button>`;
  }
  return `<p class="vtl-action-hint">Straße ist gefestigt — ohne Ansage wirken deine Präsenz-Aktionen hier nicht.</p>
    <button class="vtl-btn vtl-btn--danger" data-vtl-ansage="${esc(bp.streetId)}">ANSAGE MACHEN</button>`;
}

/** B4/Q4: ZEIGEN/TAGS/PARTY erfordern einen postierten eigenen Dealer, SPÄHER ist immer erlaubt
 * (dient zum Auskundschaften vor dem Einstieg) — Server ist die eigentliche Wahrheit (403
 * DEALER_REQUIRED), hier nur vorab visuell gesperrt + Grund genannt statt eines rohen Fehler-Toasts. */
function renderBrennpunktActionButtons(bp: BrennpunktListItem): string {
  const { cooldowns, costsCents } = bp.presenceActions;
  return getSortedActions(costsCents).map((action) => {
    if (action === 'SPAEHER' || bp.ownDealerPosted) {
      return renderPresenceActionButton(bp.streetId, action, cooldowns[action], costsCents[action]);
    }
    const cooldownIso = cooldowns[action];
    const onCooldown = cooldownIso !== null && new Date(cooldownIso).getTime() > Date.now();
    return `<button class="vtl-action-btn" disabled title="Erfordert einen postierten Dealer">
      <span>${PRESENCE_ACTION_LABELS[action]}</span>
      <span class="vtl-action-btn-cost">${onCooldown ? `NOCH ${fmtCountdownUntil(cooldownIso)} STD` : 'DEALER NÖTIG'}</span>
    </button>`;
  }).join('');
}

function renderBrennpunktCard(bp: BrennpunktListItem): string {
  const isExpanded = expandedBrennpunktId === bp.streetId;
  const distanceBadge = `<span class="vtl-badge vtl-badge--distance">${bp.distance === 0 ? 'EIGENER BEZIRK' : 'NACHBARBEZIRK'}</span>`;
  const fortifiedBadge = bp.isFortified ? `<span class="vtl-badge vtl-badge--heiss">GEFESTIGT</span>` : '';
  const ownBadge = bp.ownDealerPosted ? `<span class="vtl-badge vtl-badge--ruhig">DU POSTIERT</span>` : '';

  return `<div class="vtl-street vtl-street--brennpunkt">
    <div class="vtl-street-head" data-vtl-toggle-brennpunkt="${esc(bp.streetId)}">
      <span class="vtl-street-name">${esc(bp.streetName)} <span class="vtl-street-summary">· ${esc(bp.districtName)}</span></span>
      <div class="vtl-street-badges">
        ${distanceBadge}
        ${fortifiedBadge}
        ${ownBadge}
        ${!isExpanded ? `<span class="vtl-street-summary">${String(bp.occupiedCorners)}/${String(bp.totalCorners)} ECKEN</span>` : ''}
        <span class="vtl-street-caret">${isExpanded ? '▼' : '▶'}</span>
      </div>
    </div>
    ${isExpanded ? `<div class="vtl-street-body">
      ${renderAbsatzBar('HEISS', bp.absatzLabel)}
      <p class="vtl-other-title" style="margin-top:var(--space-2);">ECKEN</p>
      ${bp.corners.map(renderCornerRow).join('')}
      <p class="vtl-other-title" style="margin-top:var(--space-3);">TEILNEHMER</p>
      ${renderBrennpunktParticipants(bp)}
      <div class="vtl-presence" style="border-top:none;padding-top:0;">
        ${renderAnsageControl(bp)}
        <div class="vtl-action-grid" style="margin-top:var(--space-2);">${renderBrennpunktActionButtons(bp)}</div>
      </div>
    </div>` : ''}
  </div>`;
}

function renderBrennpunkteSection(): string {
  const summary = brennpunkte
    ? `${String(brennpunkte.length)} ERREICHBAR`
    : '';

  return `<div class="vtl-street vtl-street--brennpunkt">
    <div class="vtl-street-head" data-vtl-toggle-brennpunkte-section>
      <span class="vtl-street-name">BRENNPUNKTE</span>
      <div class="vtl-street-badges">
        ${!brennpunkteExpanded && summary ? `<span class="vtl-street-summary">${esc(summary)}</span>` : ''}
        <span class="vtl-street-caret">${brennpunkteExpanded ? '▼' : '▶'}</span>
      </div>
    </div>
    ${brennpunkteExpanded ? `<div class="vtl-street-body">
      ${brennpunkteError ? `<p class="vtl-empty">${esc(brennpunkteError)}</p>` : ''}
      ${brennpunkteLoading ? `<p class="vtl-empty">LADE...</p>` : ''}
      ${!brennpunkteLoading && !brennpunkteError && brennpunkte
        ? (brennpunkte.length > 0
          ? brennpunkte.map(renderBrennpunktCard).join('')
          : `<p class="vtl-empty">Kein Brennpunkt erreichbar.</p>`)
        : ''}
    </div>` : ''}
  </div>`;
}

function renderStreet(street: ViertelStreetView): string {
  const classBadgeCls = street.streetClass === 'RUHIG' ? 'vtl-badge--ruhig'
    : street.streetClass === 'BELEBT' ? 'vtl-badge--belebt' : 'vtl-badge--heiss';
  const distanceBadge = street.distanceSteps !== null
    ? `<span class="vtl-badge vtl-badge--distance">ENTF. ${String(street.distanceSteps)}</span>`
    : '';

  const isExpanded = expandedStreetIds.has(street.id);
  const bebautCount = street.parcels.filter((p) => !p.isFree).length;
  const freiCount = street.parcels.length - bebautCount;
  const summary = `${String(bebautCount)} BEBAUT · ${String(freiCount)} FREI`;

  return `<div class="vtl-street">
    <div class="vtl-street-head" data-vtl-toggle-street="${esc(street.id)}">
      <span class="vtl-street-name">${esc(street.name)}</span>
      <div class="vtl-street-badges">
        <span class="vtl-badge vtl-badge--heimat">${STREET_TYPE_LABELS[street.type]}</span>
        <span class="vtl-badge ${classBadgeCls}">${STREET_CLASS_LABELS[street.streetClass]}</span>
        ${distanceBadge}
        ${!isExpanded ? `<span class="vtl-street-summary">${esc(summary)}</span>` : ''}
        <span class="vtl-street-caret">${isExpanded ? '▼' : '▶'}</span>
      </div>
    </div>
    ${isExpanded ? `<div class="vtl-street-body">
      ${renderAbsatzBar(street.streetClass, street.presence.absatzLabel)}
      ${street.parcels.map(renderParcelRow).join('')}
      ${street.corners.length > 0 ? `
        <p class="vtl-other-title" style="margin-top:var(--space-2);">ECKEN</p>
        ${street.corners.map(renderCornerRow).join('')}
      ` : ''}
      ${renderPresenceSection(street)}
    </div>` : ''}
  </div>`;
}

// Objekte ohne Parzelle sind trotzdem verkäuflich (CAMPINGWAGEN/ALTBESTAND — Bugfix: seit
// Verkaufen ausschließlich im Viertel läuft, hatten sie gar keinen Sell-Weg mehr, blockierten
// aber unverändert MAX_HIDEOUTS/MAX_WAREHOUSES). NOTFALLGARAGE hat ihren eigenen KÜNDIGEN-Weg
// (renderEmergencyGarageBar) und bleibt hier bewusst ausgenommen.
function renderOtherRow(o: ViertelOtherPropertyView): string {
  const labelText = `${o.label}${o.nickname ? ` · ${o.nickname}` : ''}`;
  const sellable = o.kind !== 'NOTFALLGARAGE';

  let actionsHtml = '';
  if (sellable) {
    if (confirmSellParcelId === o.id) {
      actionsHtml = `
        <span class="vtl-sell-confirm">Wirklich verkaufen?</span>
        <button class="vtl-btn vtl-btn--danger" data-vtl-sell-confirm="${esc(o.id)}" ${sellSubmitting ? 'disabled' : ''}>JA</button>
        <button class="vtl-btn" data-vtl-sell-cancel>NEIN</button>`;
    } else {
      actionsHtml = `<button class="vtl-btn vtl-btn--sell" data-vtl-sell="${esc(o.id)}">VERKAUFEN</button>`;
    }
  }

  return `<div class="vtl-other-row">
    <span class="vtl-other-kind">${OTHER_KIND_LABELS[o.kind]}</span>
    <span class="vtl-parcel-label">${esc(labelText)}</span>
    ${sellable ? `<div class="vtl-parcel-actions">${actionsHtml}</div>` : ''}
  </div>`;
}

function renderEmergencyGarageBar(eg: EmergencyGarageStatusView): string {
  const rentedAtLabel = new Date(eg.rentedAt).toLocaleString('de-DE');
  const outstanding = BigInt(eg.outstandingCents);
  return `<div class="vtl-eg-bar">
    <p class="vtl-eg-title">Notfallgarage aktiv</p>
    <div class="vtl-eg-grid">
      <span class="vtl-eg-key">Seit</span><span class="vtl-eg-val">${esc(rentedAtLabel)}</span>
      <span class="vtl-eg-key">Aktueller Satz</span><span class="vtl-eg-val">${fmtEur(eg.currentHourlyRateCents)}/h</span>
      <span class="vtl-eg-key">Rückstand</span>
      <span class="vtl-eg-val${outstanding > 0n ? ' is-debt' : ''}">${fmtEur(eg.outstandingCents)}</span>
    </div>
    <button class="vtl-btn vtl-btn--cancel" data-vtl-cancel-garage="${esc(eg.id)}">KÜNDIGEN</button>
  </div>`;
}

// ─── Rendering: BEBAUEN-Modal (parzellen-first, F7) ────────────────────────────

/** "12" bzw. "12 → 18" — Basis und Maximum nach Ausbau. */
function fmtRange(r: CatalogAxisRangeView): string {
  return r.max > r.base ? `${String(r.base)} → ${String(r.max)}` : String(r.base);
}

/** NT-POLISH-01 (P9): Slot- und Ausbau-Vorschau je Typ — reine Anzeige. */
function renderBuildPreview(p: CatalogBuildPreview): string {
  const level = (n: number): string => (n > 0 ? `max. Stufe ${String(n)}` : '—');
  return `<div class="vtl-build-preview">
    <span>LAGERSLOTS <strong>${fmtRange(p.storageSlots)}</strong></span>
    <span>GROW <strong>${level(p.growMaxLevel)}</strong></span>
    <span>LABOR <strong>${level(p.laborMaxLevel)}</strong></span>
    <span>FAHRZEUGSLOTS <strong>${fmtRange(p.vehicleSlots)}</strong></span>
  </div>`;
}

function renderBebauenModal(): string {
  if (!bebauenModal) return '';
  const { propertyType, flaecheQm } = bebauenModal;

  let bodyHtml: string;
  if (catalogError) {
    bodyHtml = `<p class="vtl-empty">${esc(catalogError)}</p>`;
  } else if (catalogLoading || !catalogData) {
    bodyHtml = `<p class="vtl-empty">LADE KATALOG...</p>`;
  } else {
    const maxCount = propertyType === 'HIDEOUT' ? MAX_HIDEOUTS : MAX_WAREHOUSES;
    const items: { subType: string; label: string; minFlaecheQm: number; purchasePriceCents: string; rentPerWeekCents: string; ownedCount: number; canAfford: boolean; buildPreview: CatalogBuildPreview }[] =
      propertyType === 'HIDEOUT'
        ? catalogData.hideouts.filter((h) => h.purchasable)
        : catalogData.warehouses;

    bodyHtml = items.map((item) => {
      const tooSmall = flaecheQm < item.minFlaecheQm;
      const atMax = item.ownedCount >= maxCount;
      const blocked = tooSmall || atMax || !item.canAfford;
      let blockMsg = '';
      if (tooSmall) blockMsg = `Braucht mind. ${String(item.minFlaecheQm)} m² (diese Parzelle hat ${String(flaecheQm)} m²)`;
      else if (atMax) blockMsg = `MAXIMUM ERREICHT (${String(item.ownedCount)}/${String(maxCount)})`;
      else if (!item.canAfford) blockMsg = 'NICHT GENUG ₦';
      const buying = bebauenBuyingSubType === item.subType;

      return `<div class="vtl-bebauen-item${blocked ? ' vtl-bebauen-item--blocked' : ''}">
        <div class="vtl-bebauen-item-head">
          <span class="vtl-bebauen-item-title">${esc(item.label.toUpperCase())}</span>
          <span class="vtl-bebauen-item-price">${fmtEur(item.purchasePriceCents)}</span>
        </div>
        <p class="vtl-bebauen-item-meta">Fläche ab ${String(item.minFlaecheQm)} m² · Miete ${fmtEur(item.rentPerWeekCents)}/Woche</p>
        ${renderBuildPreview(item.buildPreview)}
        ${blockMsg ? `<p class="vtl-bebauen-item-block">${esc(blockMsg)}</p>` : ''}
        <button class="vtl-btn vtl-btn--buy" data-vtl-bebauen-buy="${esc(item.subType)}" ${blocked || bebauenBuyingSubType !== null ? 'disabled' : ''}>
          ${buying ? '...' : `${propertyType === 'HIDEOUT' ? 'VERSTECK' : 'LAGER'} HIER BAUEN`}
        </button>
      </div>`;
    }).join('') || `<p class="vtl-empty">Kein Sortiment verfügbar.</p>`;
  }

  return `<div class="modal-backdrop" id="vtl-bebauen-backdrop">
    <div class="modal">
      <div class="modal-header"><h2>${propertyType === 'HIDEOUT' ? 'VERSTECK' : 'LAGER'} BAUEN — ${String(flaecheQm)} m²</h2></div>
      <div class="modal-body">${bodyHtml}</div>
      <div class="modal-footer"><button class="btn" data-vtl-bebauen-cancel>SCHLIESSEN</button></div>
    </div>
  </div>`;
}

// ─── Rendering: Gesamt ──────────────────────────────────────────────────────────

function renderContent(): string {
  if (isLoading) return `<p class="vtl-empty">LADE...</p>`;
  if (loadError) return `<p class="vtl-empty">${esc(loadError)}</p>`;
  if (!overview) return `<p class="vtl-empty">Keine Daten.</p>`;

  // NT-VIERTEL-07-FEINSCHLIFF (Teil C): Fragezeichen springt in den Kladde-Spickzettel-Eintrag
  // "DAS VIERTEL" (kladde.constants.ts), Muster: pages/kladde.ts's ?view=…&entry=…-Deep-Link.
  const header = `<p class="vtl-header">BEZIRK // <span class="vtl-quarter">${esc(overview.districtName ?? '—')}</span>
    &nbsp;&nbsp;VIERTEL // <span class="vtl-quarter">${esc(overview.quarterName ?? '—')}</span>
    <button type="button" class="vtl-help-btn" data-vtl-help title="Was ist das Viertel?">?</button></p>`;

  const tickerHtml = renderTicker(overview.ticker);

  const eg = overview.emergencyGarage;
  const egSection = eg ? renderEmergencyGarageBar(eg) : '';

  const streetsHtml = overview.streets.map(renderStreet).join('') || `<p class="vtl-empty">Keine Straßen zugeteilt.</p>`;

  const otherWithoutGarage = overview.other.filter((o) => o.kind !== 'NOTFALLGARAGE');
  const otherHtml = otherWithoutGarage.length > 0
    ? `<div class="vtl-other-section">
        <p class="vtl-other-title">OHNE ADRESSE</p>
        ${otherWithoutGarage.map(renderOtherRow).join('')}
      </div>`
    : '';

  return `${header}${tickerHtml}${egSection}${renderBrennpunkteSection()}${streetsHtml}${otherHtml}${renderBebauenModal()}`;
}

function rerender(): void {
  const root = document.getElementById('viertel-tab-root');
  if (root) root.innerHTML = renderContent();
}

// ─── Daten laden ──────────────────────────────────────────────────────────────

async function reload(): Promise<void> {
  if (reloadPromise) return reloadPromise;

  reloadPromise = (async () => {
    try {
      overview = await api.get<ViertelOverview>('/viertel');
      loadError = null;
    } catch (err) {
      loadError = err instanceof ApiError ? err.message : 'Laden fehlgeschlagen.';
    }
    isLoading = false;
    rerender();
  })();

  try {
    await reloadPromise;
  } finally {
    reloadPromise = null;
  }
}

// NT-VIERTEL-06-BRENNPUNKTE — lazy, nur beim Aufklappen des Abschnitts geladen.
async function reloadBrennpunkte(): Promise<void> {
  brennpunkteLoading = true;
  brennpunkteError = null;
  rerender();
  try {
    brennpunkte = await api.get<BrennpunktListItem[]>('/viertel/brennpunkte');
  } catch (err) {
    brennpunkteError = err instanceof ApiError ? err.message : 'Brennpunkte laden fehlgeschlagen.';
  }
  brennpunkteLoading = false;
  rerender();
}

/** Lädt overview immer neu, brennpunkte nur, wenn der Abschnitt bereits geöffnet war — hält
 * beide Listen nach einer Ecken-Aktion (POSTIEREN/UMSIEDELN/ABZIEHEN, egal welche Liste die
 * Aktion ausgelöst hat) synchron, ohne unnötig nachzuladen, wenn Brennpunkte nie geöffnet wurden. */
async function reloadAll(): Promise<void> {
  const tasks: Promise<void>[] = [reload()];
  if (brennpunkte !== null) tasks.push(reloadBrennpunkte());
  await Promise.all(tasks);
}

async function loadCatalogIfNeeded(): Promise<void> {
  if (catalogData || catalogLoading) return;
  catalogLoading = true;
  catalogError = null;
  rerender();
  try {
    catalogData = await api.get<CatalogResponse>('/properties/catalog');
  } catch (err) {
    catalogError = err instanceof ApiError ? err.message : 'Katalog laden fehlgeschlagen.';
  }
  catalogLoading = false;
  rerender();
}

// ─── Aktionen: Notfallgarage (unverändert) ─────────────────────────────────────

async function handleCancelEmergencyGarage(id: string): Promise<void> {
  try {
    await api.delete<unknown>(`/viertel/emergency-garage/${encodeURIComponent(id)}`);
    showToast('Notfallgarage gekündigt.');
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Kündigung fehlgeschlagen.', 'error');
    rerender();
  }
}

// ─── Aktionen: ÖFFNEN (F7) ──────────────────────────────────────────────────────

function handleOpen(propertyId: string, propertyType: ViertelParcelView['propertyType']): void {
  if (!propertyId) return;
  if (propertyType === 'HIDEOUT') {
    // Innerhalb derselben Crime-Ops-Seite in die IMMOBILIEN-Sektion wechseln (Muster:
    // gewaechshaus-tab.ts's Sprung in die SCHWARZMARKT-Sektion) — kein Seitenwechsel nötig.
    sessionStorage.setItem('crimeops_hideout_id', propertyId);
    document.querySelector<HTMLButtonElement>('[data-co-section="immobilien"]')?.click();
  } else {
    // Lagerhaus ist eine eigenständige Dashboard-Seite, keine Crime-Ops-Sektion.
    navigate('/business/lagerhaus');
  }
}

// ─── Aktionen: BELIEFERN / FAHRZEUG (NT-VIERTEL-07-FEINSCHLIFF, B5) ─────────────────
// Springen ins DEALER-Tab und öffnen dort das jeweilige Panel für genau diesen Dealer — Muster
// wie handleOpen() oben. crimeOps.ts::reloadSectionData() liest die sessionStorage-Keys beim
// nächsten Laden der 'dealer'-Sektion, entfernt sie sofort danach (einmalig, Muster
// crimeops_hideout_id) und öffnet WARE-LIEFERN-Modal bzw. FAHRZEUG-Panel für den Ziel-Dealer.

function handleGoToDealerSupply(dealerId: string): void {
  if (!dealerId) return;
  sessionStorage.setItem('crimeops_dealer_supply_id', dealerId);
  document.querySelector<HTMLButtonElement>('[data-co-section="dealer"]')?.click();
}

function handleGoToDealerVehicle(dealerId: string): void {
  if (!dealerId) return;
  sessionStorage.setItem('crimeops_dealer_vehicle_id', dealerId);
  document.querySelector<HTMLButtonElement>('[data-co-section="dealer"]')?.click();
}

// ─── Aktionen: UMZIEHEN (F7) ─────────────────────────────────────────────────────

function handleStartMove(parcel: ViertelParcelView): void {
  moveOpenParcelId = parcel.id;
  moveSelectedParcelId = getFreeParcelsOfType(parcel.propertyType)[0]?.id ?? '';
  moveErrorMsg = null;
  rerender();
}

async function handleConfirmMove(parcelId: string): Promise<void> {
  const parcel = overview?.streets.flatMap((s) => s.parcels).find((p) => p.id === parcelId);
  if (!parcel?.propertyId || !moveSelectedParcelId) return;

  moveSubmitting = true;
  rerender();
  try {
    await api.post<{ propertyId: string; completesAt: string }>(
      `/properties/${encodeURIComponent(parcel.propertyId)}/move`,
      { targetParcelId: moveSelectedParcelId },
    );
    moveOpenParcelId = null;
    moveErrorMsg = null;
    showToast('Umzug gestartet.');
    await reload();
  } catch (err) {
    moveErrorMsg = err instanceof ApiError ? err.message : 'Umzug fehlgeschlagen.';
  } finally {
    moveSubmitting = false;
    rerender();
  }
}

// ─── Aktionen: VERKAUFEN (F7) ─────────────────────────────────────────────────────

async function handleConfirmSell(id: string): Promise<void> {
  // id ist entweder eine Parzellen-ID (reguläres Lager/Versteck mit festem Stellplatz) oder,
  // für Objekte ohne Parzelle (Campingwagen/ALTBESTAND), direkt die Property-ID selbst.
  const parcel = overview?.streets.flatMap((s) => s.parcels).find((p) => p.id === id);
  const other  = parcel ? null : overview?.other.find((o) => o.id === id);

  const propertyId   = parcel?.propertyId ?? other?.id ?? null;
  const propertyType = parcel?.propertyType ?? other?.propertyType ?? null;
  if (!propertyId || !propertyType) return;

  sellSubmitting = true;
  rerender();
  try {
    const path = propertyType === 'HIDEOUT'
      ? `/hideouts/${encodeURIComponent(propertyId)}`
      : `/properties/${encodeURIComponent(propertyId)}`;
    // Lager UND Verstecke erstatten 50 % des Kaufpreises (beide Endpunkte liefern refundCents).
    const res = await api.delete<{ refundCents?: string }>(path);
    confirmSellParcelId = null;
    showToast(res.refundCents ? `Verkauft — ${fmtEur(res.refundCents)} zurück.` : 'Verkauft.');
    void refreshBalance();
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Verkauf fehlgeschlagen.', 'error');
  } finally {
    sellSubmitting = false;
    rerender();
  }
}

// ─── Aktionen: ERWEITERN AUF … (Flächen-Ausbau, F3) ────────────────────────────

async function handleConfirmExpand(parcelId: string): Promise<void> {
  expandSubmitting = true;
  rerender();
  try {
    await api.post<unknown>(`/viertel/parcels/${encodeURIComponent(parcelId)}/upgrade-flaeche`, {});
    expandConfirmParcelId = null;
    showToast('Flächen-Ausbau gestartet.');
    void refreshBalance();
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Ausbau fehlgeschlagen.', 'error');
  } finally {
    expandSubmitting = false;
    rerender();
  }
}

// ─── Aktionen: BEBAUEN (parzellen-first, F7) ────────────────────────────────────

async function handleBebauenBuy(subType: string): Promise<void> {
  // Doppelklick-/Doppel-Handler-Schutz: solange ein Bau läuft, kein zweiter Request.
  if (!bebauenModal || !catalogData || bebauenBuyingSubType !== null) return;
  const { parcelId, propertyType } = bebauenModal;

  // Bugfix Bau-Dialog: Der Request wird ausschließlich aus dem Katalog-Eintrag des AKTUELLEN
  // Dialogs (Lager- ODER Versteck-Liste) gebaut — ein Key aus dem jeweils anderen Katalog wird
  // nie gesendet. Der Server prüft dasselbe noch einmal pro Endpunkt (buyProperty expectedType).
  const entry = propertyType === 'HIDEOUT'
    ? catalogData.hideouts.find((h) => h.subType === subType && h.purchasable)
    : catalogData.warehouses.find((w) => w.subType === subType);
  if (!entry) {
    showToast('Unbekannter Gebäudetyp.', 'error');
    return;
  }

  bebauenBuyingSubType = subType;
  rerender();
  try {
    if (propertyType === 'HIDEOUT') {
      await api.post<unknown>('/hideouts/purchase', { subType: entry.subType, parcelId });
    } else {
      await api.post<unknown>('/properties/buy', { subType: entry.subType, parcelId });
    }
    bebauenModal = null;
    showToast(`${entry.label} gebaut.`);
    void refreshBalance();
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Bau fehlgeschlagen.', 'error');
  } finally {
    bebauenBuyingSubType = null;
    rerender();
  }
}

// ─── Aktionen: Ecken (NT-VIERTEL-04-ECKEN) ──────────────────────────────────────

async function loadPostableDealersIfNeeded(): Promise<void> {
  if (postableDealers || postableDealersLoading) return;
  postableDealersLoading = true;
  postableDealersError = null;
  rerender();
  try {
    postableDealers = await api.get<PostableDealer[]>('/dealer');
  } catch (err) {
    postableDealersError = err instanceof ApiError ? err.message : 'Dealer laden fehlgeschlagen.';
  }
  postableDealersLoading = false;
  rerender();
}

function handleStartPost(cornerId: string): void {
  postOpenCornerId = cornerId;
  postSelectedDealerId = '';
  postableDealers = null; // frisch laden — Anwerben im Dealer-Tab kann inzwischen passiert sein
  rerender();
  void loadPostableDealersIfNeeded();
}

async function handleConfirmPost(cornerId: string): Promise<void> {
  const options = getPostableDealerOptions();
  const dealerId = options.some((d) => d.id === postSelectedDealerId) ? postSelectedDealerId : (options[0]?.id ?? '');
  if (!dealerId) return;
  postSubmitting = true;
  rerender();
  try {
    await api.post<unknown>(`/dealer/${encodeURIComponent(dealerId)}/post`, { cornerId });
    postOpenCornerId = null;
    showToast('Dealer postiert.');
    await reloadAll();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Postieren fehlgeschlagen.', 'error');
  } finally {
    postSubmitting = false;
    rerender();
  }
}

function handleStartRelocate(dealerId: string): void {
  relocateOpenDealerId = dealerId;
  relocateSelectedCornerId = getFreeCorners()[0]?.id ?? '';
  relocateErrorMsg = null;
  rerender();
}

async function handleConfirmRelocate(dealerId: string): Promise<void> {
  if (!relocateSelectedCornerId) return;
  relocateSubmitting = true;
  rerender();
  try {
    await api.post<unknown>(`/dealer/${encodeURIComponent(dealerId)}/relocate`, { cornerId: relocateSelectedCornerId });
    relocateOpenDealerId = null;
    relocateErrorMsg = null;
    showToast('Umsiedlung gestartet.');
    await reloadAll();
  } catch (err) {
    relocateErrorMsg = err instanceof ApiError ? err.message : 'Umsiedlung fehlgeschlagen.';
  } finally {
    relocateSubmitting = false;
    rerender();
  }
}

async function handleConfirmWithdraw(dealerId: string): Promise<void> {
  withdrawSubmitting = true;
  rerender();
  try {
    await api.post<unknown>(`/dealer/${encodeURIComponent(dealerId)}/withdraw`, {});
    confirmWithdrawDealerId = null;
    showToast('Dealer abgezogen.');
    await reloadAll();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Abziehen fehlgeschlagen.', 'error');
  } finally {
    withdrawSubmitting = false;
    rerender();
  }
}

// ─── Aktionen: Präsenz (NT-VIERTEL-05-PRAESENZ) ────────────────────────────────

async function handlePresenceAction(streetId: string, action: PresenceActionKey): Promise<void> {
  const key = `${streetId}:${action}`;
  if (presenceActionSubmitting.has(key)) return;
  presenceActionSubmitting.add(key);
  rerender();
  try {
    const res = await api.post<{ influenceGainedPercent: number | null }>(
      `/viertel/streets/${encodeURIComponent(streetId)}/action`, { action },
    );
    // NT-VIERTEL-FIX-01 (V2): auf Brennpunkten den tatsächlichen Zugewinn nennen.
    const gain = res.influenceGainedPercent;
    showToast(gain === null
      ? `${PRESENCE_ACTION_LABELS[action]} erledigt.`
      : `${PRESENCE_ACTION_LABELS[action]} · EINFLUSS +${gain.toFixed(1).replace('.', ',')} %`);
    void refreshBalance();
    await reloadAll();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Aktion fehlgeschlagen.', 'error');
  } finally {
    presenceActionSubmitting.delete(key);
    rerender();
  }
}

/** ANSAGE (B7) — kündigt öffentlich einen Angriff auf eine gefestigte Brennpunkt-Straße an,
 * wirksam erst nach CHALLENGE_LEAD_HOURS. Ergebnis wird session-lokal gemerkt (announcedChallenges),
 * damit renderAnsageControl() den Countdown ohne erneuten Server-Roundtrip anzeigen kann. */
async function handleConfirmAnsage(streetId: string): Promise<void> {
  ansageSubmitting = true;
  rerender();
  try {
    const result = await api.post<{ streetId: string; effectiveAt: string }>(
      `/viertel/streets/${encodeURIComponent(streetId)}/ansage`, {},
    );
    announcedChallenges.set(streetId, result.effectiveAt);
    confirmAnsageStreetId = null;
    showToast('Ansage gemacht.');
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Ansage fehlgeschlagen.', 'error');
  } finally {
    ansageSubmitting = false;
    rerender();
  }
}

async function handleConfirmTakeover(streetId: string): Promise<void> {
  takeoverSubmitting = true;
  rerender();
  try {
    await api.post<unknown>(`/viertel/streets/${encodeURIComponent(streetId)}/verdraengen`, {});
    confirmTakeoverStreetId = null;
    showToast('Fraktion verdrängt.');
    await reload();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Verdrängen fehlgeschlagen.', 'error');
  } finally {
    takeoverSubmitting = false;
    rerender();
  }
}

// ─── Event-Handling ───────────────────────────────────────────────────────────

// Listener nur einmal pro Root-Element (Muster NT-HOTFIX-01 H5): mountViertelTab() kann bei
// bestehendem Root erneut laufen — ohne Guard feuerte jeder Klick mehrfach auf altes DOM.
let listenersRoot: HTMLElement | null = null;

function attachTabListeners(): void {
  const root = document.getElementById('viertel-tab-root');
  if (!root || listenersRoot === root) return;
  listenersRoot = root;

  root.addEventListener('click', (e) => {
    const target = e.target as Element;

    // Hilfe (Teil C, NT-VIERTEL-07-FEINSCHLIFF) — springt in den Kladde-Spickzettel.
    if (target.closest('[data-vtl-help]')) {
      navigate('/kladde?view=spickzettel&entry=das_viertel');
      return;
    }

    // Notfallgarage kündigen
    const cancelGarageBtn = target.closest<HTMLButtonElement>('[data-vtl-cancel-garage]');
    if (cancelGarageBtn) {
      const id = cancelGarageBtn.dataset.vtlCancelGarage ?? '';
      cancelGarageBtn.disabled = true;
      void handleCancelEmergencyGarage(id);
      return;
    }

    // Straße auf-/zuklappen (F8)
    const streetToggle = target.closest<HTMLElement>('[data-vtl-toggle-street]');
    if (streetToggle) {
      const id = streetToggle.dataset.vtlToggleStreet ?? '';
      if (expandedStreetIds.has(id)) expandedStreetIds.delete(id);
      else expandedStreetIds.add(id);
      rerender();
      return;
    }

    // ÖFFNEN
    const openBtn = target.closest<HTMLButtonElement>('[data-vtl-open]');
    if (openBtn) {
      const propertyId = openBtn.dataset.vtlOpen ?? '';
      const propertyType = (openBtn.dataset.vtlOpenType ?? 'WAREHOUSE') as ViertelParcelView['propertyType'];
      handleOpen(propertyId, propertyType);
      return;
    }

    // UMZIEHEN öffnen
    const moveBtn = target.closest<HTMLButtonElement>('[data-vtl-move]');
    if (moveBtn) {
      const parcelId = moveBtn.dataset.vtlMove ?? '';
      const parcel = overview?.streets.flatMap((s) => s.parcels).find((p) => p.id === parcelId);
      if (parcel) handleStartMove(parcel);
      return;
    }
    if (target.closest('[data-vtl-move-cancel]')) {
      moveOpenParcelId = null;
      moveErrorMsg = null;
      rerender();
      return;
    }
    const moveConfirmBtn = target.closest<HTMLButtonElement>('[data-vtl-move-confirm]');
    if (moveConfirmBtn && !moveConfirmBtn.disabled) {
      const parcelId = moveConfirmBtn.dataset.vtlMoveConfirm ?? '';
      void handleConfirmMove(parcelId);
      return;
    }

    // VERKAUFEN
    const sellBtn = target.closest<HTMLButtonElement>('[data-vtl-sell]');
    if (sellBtn) {
      confirmSellParcelId = sellBtn.dataset.vtlSell ?? null;
      rerender();
      return;
    }
    if (target.closest('[data-vtl-sell-cancel]')) {
      confirmSellParcelId = null;
      rerender();
      return;
    }
    const sellConfirmBtn = target.closest<HTMLButtonElement>('[data-vtl-sell-confirm]');
    if (sellConfirmBtn && !sellConfirmBtn.disabled) {
      const parcelId = sellConfirmBtn.dataset.vtlSellConfirm ?? '';
      void handleConfirmSell(parcelId);
      return;
    }

    // ERWEITERN AUF … (F3)
    const expandBtn = target.closest<HTMLButtonElement>('[data-vtl-expand]');
    if (expandBtn) {
      expandConfirmParcelId = expandBtn.dataset.vtlExpand ?? null;
      rerender();
      return;
    }
    if (target.closest('[data-vtl-expand-cancel]')) {
      expandConfirmParcelId = null;
      rerender();
      return;
    }
    const expandConfirmBtn = target.closest<HTMLButtonElement>('[data-vtl-expand-confirm]');
    if (expandConfirmBtn && !expandConfirmBtn.disabled) {
      const parcelId = expandConfirmBtn.dataset.vtlExpandConfirm ?? '';
      void handleConfirmExpand(parcelId);
      return;
    }

    // BEBAUEN öffnen/schließen/kaufen (F7)
    const bebauenBtn = target.closest<HTMLButtonElement>('[data-vtl-bebauen]');
    if (bebauenBtn) {
      const parcelId = bebauenBtn.dataset.vtlBebauen ?? '';
      const propertyType = (bebauenBtn.dataset.vtlBebauenType ?? 'WAREHOUSE') as ViertelParcelView['propertyType'];
      const flaecheQm = Number(bebauenBtn.dataset.vtlBebauenFlaeche ?? '45');
      bebauenModal = { parcelId, propertyType, flaecheQm };
      rerender();
      void loadCatalogIfNeeded();
      return;
    }
    if (target.closest('[data-vtl-bebauen-cancel]')) {
      bebauenModal = null;
      rerender();
      return;
    }
    // Klick auf den Backdrop selbst (außerhalb von .modal) schließt ebenfalls.
    if (target.id === 'vtl-bebauen-backdrop') {
      bebauenModal = null;
      rerender();
      return;
    }
    const bebauenBuyBtn = target.closest<HTMLButtonElement>('[data-vtl-bebauen-buy]');
    if (bebauenBuyBtn && !bebauenBuyBtn.disabled) {
      const subType = bebauenBuyBtn.dataset.vtlBebauenBuy ?? '';
      void handleBebauenBuy(subType);
      return;
    }

    // POSTIEREN (Ecken, NT-VIERTEL-04-ECKEN)
    const postBtn = target.closest<HTMLButtonElement>('[data-vtl-post]');
    if (postBtn) {
      handleStartPost(postBtn.dataset.vtlPost ?? '');
      return;
    }
    if (target.closest('[data-vtl-post-cancel]')) {
      postOpenCornerId = null;
      rerender();
      return;
    }
    const postConfirmBtn = target.closest<HTMLButtonElement>('[data-vtl-post-confirm]');
    if (postConfirmBtn && !postConfirmBtn.disabled) {
      const cornerId = postConfirmBtn.dataset.vtlPostConfirm ?? '';
      void handleConfirmPost(cornerId);
      return;
    }

    // BELIEFERN / FAHRZEUG (NT-VIERTEL-07-FEINSCHLIFF, B5) — Sprung ins DEALER-Tab.
    const cornerSupplyBtn = target.closest<HTMLButtonElement>('[data-vtl-corner-supply]');
    if (cornerSupplyBtn) {
      handleGoToDealerSupply(cornerSupplyBtn.dataset.vtlCornerSupply ?? '');
      return;
    }
    const cornerVehicleBtn = target.closest<HTMLButtonElement>('[data-vtl-corner-vehicle]');
    if (cornerVehicleBtn) {
      handleGoToDealerVehicle(cornerVehicleBtn.dataset.vtlCornerVehicle ?? '');
      return;
    }

    // UMSIEDELN
    const relocateBtn = target.closest<HTMLButtonElement>('[data-vtl-relocate]');
    if (relocateBtn) {
      handleStartRelocate(relocateBtn.dataset.vtlRelocate ?? '');
      return;
    }
    if (target.closest('[data-vtl-relocate-cancel]')) {
      relocateOpenDealerId = null;
      relocateErrorMsg = null;
      rerender();
      return;
    }
    const relocateConfirmBtn = target.closest<HTMLButtonElement>('[data-vtl-relocate-confirm]');
    if (relocateConfirmBtn && !relocateConfirmBtn.disabled) {
      const dealerId = relocateConfirmBtn.dataset.vtlRelocateConfirm ?? '';
      void handleConfirmRelocate(dealerId);
      return;
    }

    // ABZIEHEN
    const withdrawBtn = target.closest<HTMLButtonElement>('[data-vtl-withdraw]');
    if (withdrawBtn) {
      confirmWithdrawDealerId = withdrawBtn.dataset.vtlWithdraw ?? null;
      rerender();
      return;
    }
    if (target.closest('[data-vtl-withdraw-cancel]')) {
      confirmWithdrawDealerId = null;
      rerender();
      return;
    }
    const withdrawConfirmBtn = target.closest<HTMLButtonElement>('[data-vtl-withdraw-confirm]');
    if (withdrawConfirmBtn && !withdrawConfirmBtn.disabled) {
      const dealerId = withdrawConfirmBtn.dataset.vtlWithdrawConfirm ?? '';
      void handleConfirmWithdraw(dealerId);
      return;
    }

    // Präsenz-Aktion (NT-VIERTEL-05-PRAESENZ, P3)
    const presenceActionBtn = target.closest<HTMLButtonElement>('[data-vtl-presence-action]');
    if (presenceActionBtn && !presenceActionBtn.disabled) {
      const streetId = presenceActionBtn.dataset.vtlPresenceAction ?? '';
      const action = presenceActionBtn.dataset.vtlPresenceActionKey as PresenceActionKey;
      void handlePresenceAction(streetId, action);
      return;
    }

    // VERDRÄNGEN (P7)
    const takeoverBtn = target.closest<HTMLButtonElement>('[data-vtl-takeover]');
    if (takeoverBtn) {
      confirmTakeoverStreetId = takeoverBtn.dataset.vtlTakeover ?? null;
      rerender();
      return;
    }
    if (target.closest('[data-vtl-takeover-cancel]')) {
      confirmTakeoverStreetId = null;
      rerender();
      return;
    }
    const takeoverConfirmBtn = target.closest<HTMLButtonElement>('[data-vtl-takeover-confirm]');
    if (takeoverConfirmBtn && !takeoverConfirmBtn.disabled) {
      const streetId = takeoverConfirmBtn.dataset.vtlTakeoverConfirm ?? '';
      void handleConfirmTakeover(streetId);
      return;
    }

    // BRENNPUNKTE-Abschnitt auf-/zuklappen — lazy Erstladung beim ersten Öffnen (NT-VIERTEL-06)
    if (target.closest('[data-vtl-toggle-brennpunkte-section]')) {
      brennpunkteExpanded = !brennpunkteExpanded;
      rerender();
      if (brennpunkteExpanded && brennpunkte === null) void reloadBrennpunkte();
      return;
    }

    // Einzelnen Brennpunkt auf-/zuklappen (nur einer gleichzeitig)
    const brennpunktToggle = target.closest<HTMLElement>('[data-vtl-toggle-brennpunkt]');
    if (brennpunktToggle) {
      const id = brennpunktToggle.dataset.vtlToggleBrennpunkt ?? '';
      expandedBrennpunktId = expandedBrennpunktId === id ? null : id;
      rerender();
      return;
    }

    // ANSAGE (B7)
    const ansageBtn = target.closest<HTMLButtonElement>('[data-vtl-ansage]');
    if (ansageBtn) {
      confirmAnsageStreetId = ansageBtn.dataset.vtlAnsage ?? null;
      rerender();
      return;
    }
    if (target.closest('[data-vtl-ansage-cancel]')) {
      confirmAnsageStreetId = null;
      rerender();
      return;
    }
    const ansageConfirmBtn = target.closest<HTMLButtonElement>('[data-vtl-ansage-confirm]');
    if (ansageConfirmBtn && !ansageConfirmBtn.disabled) {
      const streetId = ansageConfirmBtn.dataset.vtlAnsageConfirm ?? '';
      void handleConfirmAnsage(streetId);
    }
  });

  root.addEventListener('change', (e) => {
    const target = e.target as HTMLElement;
    if ((target as HTMLSelectElement).matches('[data-vtl-move-select]')) {
      moveSelectedParcelId = (target as HTMLSelectElement).value;
    }
    if ((target as HTMLSelectElement).matches('[data-vtl-post-select]')) {
      postSelectedDealerId = (target as HTMLSelectElement).value;
    }
    if ((target as HTMLSelectElement).matches('[data-vtl-relocate-select]')) {
      relocateSelectedCornerId = (target as HTMLSelectElement).value;
    }
  });
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

/** Initiales HTML-Skelett — wird von renderCurrentSection() in crimeOps.ts eingebaut */
export function renderViertelTab(): string {
  return `<div id="viertel-tab-root" style="padding:var(--space-2) 0;">
    <p class="vtl-empty">LADE...</p>
  </div>`;
}

/** Aufgerufen wenn crimeOps die Sektion 'viertel' aktiviert */
export function mountViertelTab(): void {
  addStyles();
  if (refreshTimer !== null) { clearInterval(refreshTimer); refreshTimer = null; }
  isLoading = overview === null;
  loadError = null;
  if (!isLoading) rerender();
  attachTabListeners();
  void reload();
  refreshTimer = setInterval(() => { void reload(); }, 30_000);
}

/** Aufgerufen wenn crimeOps die Sektion verlässt */
export function cleanupViertelTab(): void {
  if (refreshTimer !== null) { clearInterval(refreshTimer); refreshTimer = null; }
  overview = null;
  loadError = null;
  isLoading = true;
  expandedStreetIds.clear();
  catalogData = null;
  catalogLoading = false;
  catalogError = null;
  bebauenModal = null;
  bebauenBuyingSubType = null;
  moveOpenParcelId = null;
  moveSelectedParcelId = '';
  moveErrorMsg = null;
  moveSubmitting = false;
  confirmSellParcelId = null;
  sellSubmitting = false;
  expandConfirmParcelId = null;
  expandSubmitting = false;
  postableDealers = null;
  postableDealersLoading = false;
  postableDealersError = null;
  postOpenCornerId = null;
  postSelectedDealerId = '';
  postSubmitting = false;
  relocateOpenDealerId = null;
  relocateSelectedCornerId = '';
  relocateErrorMsg = null;
  relocateSubmitting = false;
  confirmWithdrawDealerId = null;
  withdrawSubmitting = false;
  presenceActionSubmitting.clear();
  confirmTakeoverStreetId = null;
  takeoverSubmitting = false;
  brennpunkteExpanded = false;
  brennpunkte = null;
  brennpunkteLoading = false;
  brennpunkteError = null;
  expandedBrennpunktId = null;
  confirmAnsageStreetId = null;
  ansageSubmitting = false;
  announcedChallenges.clear();
}

export async function refreshViertelTab(): Promise<void> {
  await reload();
}
